// Energy — TRON energy for the desk's wallets, shown in the Books.
//
// A USDT transfer costs energy. A wallet without it burns TRX instead — about
// 6.5 TRX a transfer, 13 to an address that has never held USDT. Rented, the
// same energy costs about a third less. The ENERGY DESK does the renting: a
// separate service on the desk Mac (energy/ in this repository) with its own
// database and its own account at the provider. It is funded by a prepaid
// balance there — topped up by sending TRX to one address — and has the energy
// delegated to the wallets by ADDRESS. It holds no key to any wallet.
//
// Operator, 1 Oct 2026: "Use clean and separate infra for that. Do not mix
// running operational wallets or any systems with this." and "only a
// dedicated space in my books — will fund using a dedicated address and
// allocates energy to treasury wallets." So this screen is that space, and it
// is a WINDOW: every figure and every decision here is the energy desk's,
// read through the backend's energy route (bff/src/routes/energy.ts). The
// Books keep no table, no posting and no clock for it.
//
// WHAT IT SHOWS: what needs a person, first; the TronSave balance and where to
// top it up; the last 24 hours' spend against its limit; what a rental costs
// right now; each wallet's energy as the chain has it; the rentals.
//
// WHAT IT DOES, for a full-access admin: top a wallet up (price first — the
// price shown is the price the order is held to), set a wallet's standing
// allocation, list or pause a wallet, and settle by hand an order whose
// outcome the desk could not learn. A read-only admin sees everything and can
// ask a price.
//
// TWO THINGS THE SCREEN IS CAREFUL ABOUT:
//   · It never shows an old figure as a current one. The balance and each
//     wallet's energy carry when they were read; if the desk has stopped
//     making its passes, the screen says so before anything else.
//   · A top-up is ordered from the QUOTE on screen — the amount that was
//     priced, the price that was shown, and a key minted with them — never
//     from whatever the form holds at the moment of the click. Editing the
//     amount drops the quote.
//
// THE PHONE: below lg each table is a list of cards on the Ledger's card
// seats, as on KYT; the tables stand at lg+.
//
// LEANER, 18 Oct 2026 (the operator: "screen looks unnecessary bulky"). Every
// explanation lives in "About this screen" — the panels and the tiles carry
// figures and warnings, not sentences. ONE ROW PER WALLET. The forms stay
// folded until Top up or Edit. And a top-up can be rented for 1 hour, 3, 15 or
// 30 days — the terms the desk says it takes.
//
// THE DESIGN-SYSTEM SWEEP, 22 Oct 2026 ("a human is going to use them"): in
// three seconds he sees what needs him, and the button is beside it.
//   · ONE "Needs you" card above the tiles — the orders with no answer, the
//     desk's alarms, an order found on the desk — each ONE line, the whole
//     sentence behind "Details", the button on the line. A desk that is calm
//     has no such card. (The desk's sentences are shortened by
//     lib/energyOrder.ts PART FIVE, which never drops the consequence.)
//   · Every money guard is still on the screen, as one line with its detail
//     behind a tap: "may have been placed and charged — do not order again",
//     "No answer" red, an order the provider took is paid for, a quote dated
//     and amber once the picture OR the quote itself is old (it has its own age:
//     lib/energyOrder.ts quoteStale), the register's no, the same order / a NEW
//     order. An order found on the desk is RED until the desk's own record says
//     what became of it (outcomeKnown); a "Settle" link goes to the rental it
//     NAMES (every row carries data-rental, the link's data-jump says which) —
//     never to the first row that has buttons. tests/energy-screen pins the
//     phrases and DRAWS the screen (render-harness.mjs) to check the wiring.
//   · A failed read is never drawn like a calm one: red when there is nothing
//     to show, amber "read failed" in the wallet's own cell, "last known" on
//     the balance tile.
//   · Rentals: what needs him first, then the last five; the rest behind
//     "All rentals". Wallet names wrap — a name is never cut off.
import { Fragment, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { onlineManager, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, RefreshCw } from 'lucide-react';
import {
  api, type EnergyAlarm, type EnergyPreview, type EnergyRental, type EnergyRentAnswer, type EnergyRentalState,
  type EnergyStatus, type EnergyWallet,
} from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { errorWords } from '../lib/errors';
import { Card, EmptyState, Label, MoreDetails, Pending, StateLine, shortAddress, type StateTone } from '../components/kit';
import { Pill, type PillTone } from '../components/Pill';
import { LEDGER } from '../components/Ledger';
import { CopyRef } from '../components/CopyRef';
import { Field, FieldRow } from '../components/Field';
import { fmtAmount, fmtDay, fmtRelative, fmtStamp } from '../lib/format';
import { energyFreshness } from '../lib/energyFreshness';
import { readState } from '../lib/read-state';
import {
  alarmAbout, alarmLead, alarmNamesRental, alarmRentalIds, arrangeInitial, arrangeKeyOf, arrangeRetries, createStore, listName, noteLead, orderOutcome, percent, ordersInitial, ordersReduce,
  outcomeKnown, quoteStale, reasonLead, registerWhy, rentalNeedsHim, settledNotCharged, settleTarget, standingUnsaved, topUpAskable, topUpAsked, topUpHeldBack, topUpInitial,
  topUpOrder, topUpTerms, termWords,
  termToSend, trx, rateTrx, dailyCostSun, burnPriceState, TOP_UP_CHOICES, SENDS_A_DAY, SEND_ENERGY, energyForSendsADay, sendsADayOf, standingEstimate,
  type ArrangeAction, type EnergyOrders, type EnergyOrdersAction, type OrderOutcome, type TopUpAction, type TopUpTarget,
} from '../lib/energyOrder';

// ── Figures ─────────────────────────────────────────────────────────────────
// Amounts arrive as digits: sun for TRX, whole units for energy. TRX and savings as the house style says them, on
// every energy screen (lib/energyOrder trx and percent: at most two decimals, half up — N-084, 30 Oct 2026); a price
// per day or per transfer is a rate, to the sun (rateTrx — N-090), and the burn price is whole sun a unit.
export { trx };
export const units = (v: string | null | undefined): string =>
  (v === null || v === undefined ? '—' : fmtAmount(Number(v), undefined, 0));
export const saves = percent;
/** An address as the desk shows it: first eight, last four (the kit's). The whole one is what Copy takes. */
export const mask = shortAddress;
export const capital = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
/**
 * A wallet's name on a line of ten words: a register name whole, in the list form ("Hot TRC20 Oct", N-091 — lib/energyOrder.ts
 * listName); any other, its first two words and "…" when there are more — the whole name is behind the tap.
 */
export const lineName = (label: string): string => {
  const listed = listName(label);
  if (listed !== label.trim()) return listed;
  const w = label.trim().split(/\s+/);
  return w.length <= 2 ? label.trim() : `${w.slice(0, 2).join(' ')}…`;
};

// ── The Books' register ─────────────────────────────────────────────────────
// The operator, 4 Oct 2026 (merge decision D6): "D6 books register". The Books
// ask the desk to spend only on a LIVE TRON WALLET of their register —
// they check it before an order, and before a change that would let the desk
// spend (bff/src/lib/energy.ts). The screen says, for every wallet, what the
// register says of it, and holds back what would be refused.
export type RegisterState =
  | { kind: 'live'; name: string; booksWalletId: number }
  | { kind: 'not_live'; reason: string; retiredAt: string | null }
  | { kind: 'unknown' };

export function registerOf(d: EnergyStatus, w: EnergyWallet): RegisterState {
  const r = d.register;
  if (r === undefined || 'error' in r) return { kind: 'unknown' };
  const v = r.wallets[String(w.id)];
  if (v === undefined) return { kind: 'unknown' };
  return v.live ? { kind: 'live', name: v.name, booksWalletId: v.booksWalletId } : { kind: 'not_live', reason: v.reason, retiredAt: v.retiredAt };
}
/**
 * A wallet as every energy screen names it: the Books' register's name, Group · Network · Tag ("Hot · TRC20 · Oct"),
 * made by one function there (N-091, his "ofc", 30 Oct 2026: one name per wallet, everywhere; nobody types a label). A
 * wallet the register does not vouch for, or a register that could not be read, keeps the label it is on the desk under.
 */
export function nameOf(d: EnergyStatus | undefined, w: EnergyWallet): string {
  const r = d?.register;
  const v = r !== undefined && !('error' in r) ? r.wallets[String(w.id)] : undefined;
  return v !== undefined && v.live ? v.name : w.label;
}
/** The desk's wallets as the alarms' words are resolved against them (lib/energyOrder.ts alarmAbout): by their names. */
export const namedWallets = (d: EnergyStatus | undefined): Array<{ id: number; label: string }> =>
  (d?.wallets ?? []).map((w) => ({ id: w.id, label: nameOf(d, w) }));
/** Why the Books will not ask the desk to spend on this wallet — or null when they will. One sentence. */
export function holdBack(s: RegisterState): string | null {
  if (s.kind === 'live') return null;
  if (s.kind === 'unknown') return 'The Books\' wallet register could not be checked for this wallet.';
  return `Not a live wallet in the Books — ${s.reason}${s.retiredAt ? ` on ${fmtDay(s.retiredAt, true)}` : ''}.`;
}
/** The register's no as the short line a row carries: its reason's first clause, and the day when it is a retirement. */
export function registerLine(s: RegisterState): string | null {
  if (s.kind === 'live') return null;
  if (s.kind === 'unknown') return 'Books\' register unreadable';
  return `${registerWhy(s.reason)}${s.retiredAt ? ` · ${fmtDay(s.retiredAt)}` : ''}`;
}
/** The register itself could not be read: then no wallet is added, topped up or given a standing figure from the screen. */
export const registerUnreadable = (d: EnergyStatus): boolean => d.register === undefined || 'error' in d.register;

/** How long a rental runs: "1 hour", "3 days" — the shared wording (lib/energyOrder.ts). */
export const lasts = termWords;
/** A length of time ahead, in fmtRelative's own units: "40s", "12m", "5h", "3d". */
export function span(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3_600) return `${Math.round(s / 60)}m`;
  if (s < 86_400) return `${Math.round(s / 3_600)}h`;
  return `${Math.round(s / 86_400)}d`;
}
/** "ends in 2d" — or, for a lease whose end has come and the desk has not yet marked, "ending now" (never "ends in 0s"). */
export const endsIn = (seconds: number): string => (seconds <= 0 ? 'ending now' : `ends in ${span(seconds)}`);
/** From the desk's own "now" (asOf), so the figure does not drift with the viewer's clock. */
export const secondsUntil = (iso: string, asOf: string): number => (Date.parse(iso) - Date.parse(asOf)) / 1000;

/** A moment on the viewer's clock, as the Books write a time. Empty when there is none. */
export const stamp = (ms: number | null): string => (ms === null ? '' : fmtStamp(new Date(ms).toISOString()));
/** How long ago a reading was, as its own fact: "14s ago" — or, from a viewer whose clock is set back (an age below zero is no
 *  age), "at 21:43". */
export const readAgo = (iso: string): string => (Date.now() - Date.parse(iso) < -5_000 ? `at ${fmtStamp(iso)}` : fmtRelative(iso));
/** "read 14s ago" — or "read at 21:43". */
export const readWords = (iso: string): string => `read ${readAgo(iso)}`;

/** What is said when the desk sent something back that is not an answer to the order (lib/energyOrder.ts, orderOutcome). */
export const UNREADABLE_ANSWER = 'The energy desk sent back something that is not an answer to this order.';

/** A fresh key for one priced order. Sent again on a retry, so twice means once. */
export const mintKey = (what: string, walletId: number): string =>
  `${what}-${walletId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const explorerAddress = (network: EnergyStatus['network'], address: string): string =>
  `https://${network === 'nile' ? 'nile.' : ''}tronscan.org/#/address/${address}`;

// A rental's state in the desk's words → the operator's.
export const STATE_WORD: Record<EnergyRentalState, string> = {
  requested: 'Sending', ordered: 'Ordered', delegated: 'Delivered', expired: 'Ended',
  refused: 'Refused', unconfirmed: 'No answer', closed: 'Settled',
};
export const STATE_TONE: Record<EnergyRentalState, PillTone> = {
  requested: 'warn', ordered: 'warn', delegated: 'good', expired: 'neutral',
  refused: 'bad', unconfirmed: 'bad', closed: 'neutral',
};
/**
 * A rental's state as ONE tag on the rebuilt screen — its row and its sheet say the same. One that needs him says what it needs:
 * delivered and not on the wallet yet, paid for and not delivered — never "Delivered" in green for energy the wallet has not shown;
 * the rest the desk's own word (no answer among them, in red: the line at the top says it may have been charged, with its Settle).
 */
export function rentalSeat(r: Pick<EnergyRental, 'state' | 'chainSeenAt' | 'settleable'>): { word: string; tone: PillTone } {
  if (r.state === 'delegated' && r.chainSeenAt === null) return { word: 'Not on wallet', tone: 'warn' };
  if (r.settleable && r.state === 'ordered') return { word: 'Not delivered', tone: 'warn' };
  return { word: STATE_WORD[r.state], tone: STATE_TONE[r.state] };
}
/** A state in the middle of a sentence: "no answer", "delivered". */
export const stateIn = (state: string): string => (STATE_WORD[state as EnergyRentalState] ?? state).toLowerCase();

/**
 * What a rental's TRX column says. The provider's own figure when it gave one; 0 for an order that was refused or settled as not
 * charged (nothing was spent); otherwise the QUOTED price, as "at most" — an order with no answer, an order not yet filled, a
 * rental settled by hand as charged: it may have cost that much, and "≤" says so. (A refused 4.371 read as a cost.)
 */
export function rentalTrx(r: EnergyRental): { text: string; tip: string } {
  if (r.chargedSun !== null) return { text: trx(r.chargedSun), tip: 'What the provider charged' };
  // Nought is nought (design system §5): an order that was refused bought nothing; one settled by hand as "not charged" spent nothing.
  if (r.state === 'refused') return { text: '0', tip: 'Nothing was bought' };
  if (r.state === 'closed' && settledNotCharged(r.detail)) return { text: '0', tip: 'Settled as not charged' };
  return { text: `≤ ${trx(r.costSun)}`, tip: 'The quoted price — the most it can cost; the provider has given no figure' };
}
/** Has this rental anything to say beside it: the desk's note, a delivery the wallet has not shown, a button? */
const hasNote = (r: EnergyRental): boolean => Boolean(r.detail) || (r.state === 'delegated' && r.chainSeenAt === null) || r.settleable;

// ── Seats shared by the sections (KYT's) ────────────────────────────────────
const CARD = `${LEDGER.card} px-3 last:border-b-0`;
const CARD_LINE2 = `${LEDGER.cardLine2} flex-wrap gap-y-1`;
const SECTION_HEAD = 'flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2 border-b border-stroke-soft';
const TH = 'px-3 py-1.5';
const TD = 'px-3 py-2 align-top';
/** A line that must be seen on a slab of its own: red = failed or refused, amber = needs a look. (The tokens are already translucent — no `/NN`.) */
const SLAB_BAD = 'rounded-lg bg-neg-bg px-3 py-2';
const SLAB_WARN = 'rounded-lg bg-warn-bg px-3 py-2';
/** The small buttons of a row; on a phone a press is 44 px. */
const SMALL_BTN = 'btn-secondary text-meta py-1 px-2 max-lg:min-h-11 max-lg:px-3';
const PANEL_BTN = 'text-body py-1.5 disabled:opacity-50 max-lg:min-h-11';

/** Bring the first element on screen that matches into view — the table and the cards are two renderings of one list, one of them hidden. */
function showMe(selector: string): void {
  const el = [...document.querySelectorAll<HTMLElement>(selector)].find((e) => e.getBoundingClientRect().width > 0);
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * A column wide enough for its widest figure — a number is never broken across two lines. Mono is 8.4 px a character at body size,
 * the sans second line about 6.4, and a cell takes 24 px of padding; never narrower than `min`. (The tables are fixed-layout, so a
 * nine-digit balance has to be given its room; the name's column and the last, empty one take the rest.)
 */
const colPx = (mono: string[], sans: string[], min: number): number =>
  Math.max(min, Math.ceil(Math.max(0, ...mono.map((t) => t.length * 8.4), ...sans.map((t) => t.length * 6.4))) + 24);

/** A TRX figure and its muted unit (docs/DESIGN-SYSTEM.md §5). */
const inTrx = (figure: string): ReactNode => <>{figure} <span className="font-normal text-tg-hint">TRX</span></>;

/** Half-height tile, one figure each (KYT's). A figure wraps rather than being cut off. */
function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: 'warn' | 'bad' | 'good' }) {
  const color = tone === 'bad' ? 'text-neg' : tone === 'warn' ? 'text-warn' : tone === 'good' ? 'text-pos' : '';
  return (
    <div className="card px-2.5 py-1.5 min-w-0">
      <div className="text-label uppercase tracking-wide text-tg-hint">{label}</div>
      <div className={`text-body font-semibold tabular break-words ${color}`}>{value}</div>
    </div>
  );
}

/** A wallet's address: shortened, the full one on the copy button, and the explorer as an icon. */
function Address({ address, network }: { address: string; network: EnergyStatus['network'] }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <CopyRef value={address} label={mask(address)} />
      <a href={explorerAddress(network, address)} target="_blank" rel="noreferrer" className="-m-2 p-2 text-info hover:text-tg-text" title="Open in the chain explorer" aria-label="Open in the chain explorer">
        <ExternalLink size={12} />
      </a>
    </span>
  );
}

/**
 * A link that scrolls to ONE rental's row. Which rental is in the markup (`data-jump`) and the click reads it from there, so what
 * the link targets can be checked as well as what it says: never "the first row that can be settled" — that is another rental's
 * buttons (Astra, 22 Oct 2026).
 */
function ToRental({ id, children }: { id: number; children: ReactNode }) {
  return (
    <button type="button" className="btn-link" data-jump={id}
      onClick={(e) => showMe(`[data-rental="${e.currentTarget.dataset.jump}"]`)}>
      {children}
    </button>
  );
}

/** A sentence the desk or the Books said, as ONE line: its first clause, the whole of it behind a tap (lib/energyOrder.ts, PART FIVE). */
export function Said({ text, tone = 'bad', prefix, action }: { text: string; tone?: StateTone; prefix?: string; action?: ReactNode }) {
  if (text.trim() === '') return null;
  // With a word of our own in front of it, the sentence's first clause is shorter.
  const { lead, folded } = reasonLead(text, prefix ? 8 : 12);
  return <StateLine tone={tone} more={folded ? text : undefined} action={action}>{prefix ? `${prefix} ${lead}` : lead}</StateLine>;
}

// The screen's memory of orders: one state per wallet for the top-up panel and
// for "arrange it now", and the orders that got no answer. OUTSIDE any
// component — so closing a panel, opening another wallet's, turning the phone
// (the table and the cards are two renderings of one panel) or leaving this
// screen and coming back does not forget an order that is on its way, or one
// that may have been placed. The rules are lib/energyOrder.ts.
export const orders = createStore<EnergyOrders<EnergyPreview>, EnergyOrdersAction<EnergyPreview>>(ordersReduce, ordersInitial<EnergyPreview>());
export const useOrders = (): EnergyOrders<EnergyPreview> => useSyncExternalStore(orders.subscribe, orders.get, orders.get);
/** For the render checks (tests/energy-screen/render-harness.mjs): the screen's memory of orders, so a check can put it in a state without a click. */
export const energyOrdersForTests = orders;

// ── About energy — the screen's explanation, behind a tap. One copy: today's "About this screen", the rebuilt desk sheet, and the
// paragraphs the rebuilt pages' asides take. `d` is undefined until the desk has been read; its figures are left out until then.

/** What every order counts against: the limit on any 24 hours, from the moment an order is sent, and the most one order costs. */
export function AboutSpend({ d }: { d: EnergyStatus }) {
  return (
    <>
      Every order counts against the limit on any 24 hours
      ({trx(d.spend.dailyCapSun)} TRX) from the moment it is sent — its whole cost at once, however long
      it runs — and one order costs at most {trx(d.spend.orderCapSun)} TRX.
    </>
  );
}

/** What Rent energy does, and how the order is held to its price. */
export function AboutTopUp({ d }: { d?: EnergyStatus }) {
  return (
    // words:ok — explanation behind a tap: drawn only inside "About this screen" (help=), the desk's folded About and the Top up aside's
    <p>
      <strong>Rent energy</strong> makes sure a wallet has energy for transfers: 65,000 for one transfer,
      131,000 if the receiving address has never held USDT. It can be rented for 1 hour or for days:
      energy refills every 24 hours, so a longer term is that energy every day of it, and it is weighed
      against burning one transfer a day. The desk rents only what the wallet is short
      {d && <>, and only when that saves at least {saves(d.policy.minSavingBps)} against burning</>}. The
      price is checked first, and the order is held to it.
    </p>
  );
}

/** What a standing figure keeps, and whether the desk renews it by itself. */
export function AboutStanding({ d }: { d?: EnergyStatus }) {
  return (
    // words:ok — explanation behind a tap: drawn only inside "About this screen" (help=), the desk's folded About and the settings aside's
    <p>
      A <strong>standing</strong> figure keeps that much energy on a wallet day after day — for a wallet
      that sends every day{d && <>, from {units(d.policy.minEnergy)} to {units(d.policy.maxEnergy)} energy, or 0 for none</>}.{d && (d.policy.standingEnabled
        ? <> The desk renews it by itself, {lasts(d.policy.standingDurationSec)} at a time, before it runs out.</>
        : <> The desk is set not to renew it by itself: the figure is kept, and &ldquo;Arrange&rdquo; rents it once.</>)}
    </p>
  );
}

/**
 * The whole explanation. `rentalsHere`: the rentals and the funding address are on this page, under the tiles (today's screen) —
 * or the reader is in the desk's sheet, the address above it and each rental a row of the list.
 */
export function AboutEnergy({ d, rentalsHere }: { d?: EnergyStatus; rentalsHere: boolean }) {
  return (
    <>
      <p>
        A USDT transfer needs energy. Without it the wallet burns TRX instead; rented, the same energy
        costs about a third less. The energy desk rents it and has it delegated to the wallets below. It
        holds no key to any wallet: it only ever needs a wallet&apos;s address.
      </p>
      <p>
        It is paid for from the TronSave balance; to top it up, send TRX to the
        address {rentalsHere ? 'under the tiles' : 'above'}.{d && <> <AboutSpend d={d} /></>}
      </p>
      <AboutTopUp d={d} />
      <AboutStanding d={d} />
      <p>
        The wallets come from the Books&apos; wallet register. Only a TRON wallet that
        is live there — not retired, not held back by screening — can be added, topped up or given a standing
        figure here, and the address listed is the register&apos;s.{d?.activation?.enabled && <> A wallet that has
        never held TRX cannot take energy: the desk has the provider activate it by itself
        ({trx(d.activation.costSun)} TRX).</>}
      </p>
      {rentalsHere ? (
        <p>
          An order the provider gives no answer to shows as <em>No answer</em>: it may have been charged.
          Check the provider&apos;s own order list, then settle it under Rentals — which lists the newest 40,
          and every one still unsettled; the five newest others show, the rest are under &ldquo;All rentals&rdquo;.
        </p>
      ) : (
        <p>
          An order the provider gives no answer to shows as <em>No answer</em>: it may have been charged.
          Check the provider&apos;s own order list, then settle it from its rental. The desk lists the newest 40
          rentals, and every one still unsettled.
        </p>
      )}
      {d && (
        <p>
          {d.burn && d.burn.energyFeeSun !== null && burnPriceState(d.burn) !== 'unread'
            ? (burnPriceState(d.burn) === 'last-known'
              ? <>The chain&apos;s burn price cannot be read just now; the last known is {units(d.burn.energyFeeSun)} sun a unit of energy{d.burn.at && <>, read {fmtRelative(d.burn.at)}</>}.</>
              : <>Burning costs {units(d.burn.energyFeeSun)} sun a unit of energy{d.burn.at && <> (read {fmtRelative(d.burn.at)})</>}.</>)
            : <>The chain&apos;s burn price has not been read.</>}{' '}
          Balance {d.provider.checkedAt ? <>read {fmtRelative(d.provider.checkedAt)}</> : 'not read yet'}
          {d.prices && <>; prices asked {fmtRelative(d.prices.at)}</>}.
        </p>
      )}
    </>
  );
}

/**
 * THE DESK'S ONE READ, as every energy screen needs it — today's and the rebuilt one (screens/v2/energy, behind uiV2(4)): the
 * same key and the 20 s polling; `askedAtMs`, when this reading was ASKED FOR, on the viewer's clock (what it says about an order
 * that got no answer depends on which came first); `me` for full access; the viewer's clock, ticking; what each reading tells the
 * memory of orders; and whether the picture is current (lib/energyFreshness.ts). Nothing is decided here.
 */
export function useDesk() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['energy', 'status'],
    // `askedAtMs`: when this reading was ASKED FOR, on the viewer's clock. What it
    // says about an order that got no answer depends on which came first.
    queryFn: async () => { const askedAtMs = Date.now(); return { ...(await api.energyStatus()), askedAtMs }; },
    refetchInterval: 20_000,
  });
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const canChange = me.data?.role === 'full';
  const d = q.data;

  // The viewer's own clock, ticking. Without it nothing on this screen moves
  // while the polling is paused — the device offline, the tab asleep: the last
  // answer sits there, "read 4s ago", for as long as nobody touches it.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 5_000);
    return () => window.clearInterval(id);
  }, []);

  // What the desk's own list says about orders that got no answer, each time it
  // is read: the ones it holds are named; for the rest, that it was looked at
  // and when (lib/energyOrder.ts, PART THREE).
  useEffect(() => {
    if (d) orders.dispatch({ type: 'desk_record', rentals: d.rentals, askedAtMs: d.askedAtMs });
  }, [d]);

  // Back online: read now. The read client can miss the browser's "offline" (it starts every page online), and then it never
  // hears that the connection is back — the polling would find it, up to 20 s later.
  useEffect(() => {
    const back = () => void qc.invalidateQueries({ queryKey: ['energy'] });
    window.addEventListener('online', back);
    return () => window.removeEventListener('online', back);
  }, [qc]);

  // Are these figures current? Two ways they may not be — lib/energyFreshness.ts:
  // the desk stopped making its passes, or this screen stopped being able to read it.
  const fresh = d ? energyFreshness({ asOf: d.asOf, tickAt: d.tick?.at ?? null, tickSec: d.tickSec, nowMs, receivedAtMs: q.dataUpdatedAt }) : null;
  const refresh = () => void qc.invalidateQueries({ queryKey: ['energy'] });
  return { q, d, canChange, nowMs, fresh, refresh };
}
export type Desk = ReturnType<typeof useDesk>;

/**
 * WHAT NEEDS HIM, read from the desk's answer and the memory of orders — one copy for every energy screen. The alarms the
 * screen says elsewhere are left out (coveredElsewhere); the orders that got no answer are red until the desk's list was read
 * since, an order found on the desk is red until its record says what became of it; every link names the rental it is about.
 */
export function useNeeds({ d, fresh, canChange }: Pick<Desk, 'd' | 'fresh' | 'canChange'>) {
  const o = useOrders();
  const labelOf = (walletId: number): string => { const w = d?.wallets.find((x) => x.id === walletId); return w ? nameOf(d, w) : `wallet ${walletId}`; };
  const level = (key: string) => d?.alarms.find((a) => a.key === key)?.level;
  const spendCapped = d ? Number(d.spend.countedSun) >= Number(d.spend.dailyCapSun) : false;
  /** An alarm the screen already says elsewhere: the tiles carry a low balance and a spend under the limit — figure and tone — and the "desk has stopped" line the desk's rounds. */
  const coveredElsewhere = (a: EnergyAlarm): boolean => {
    if (!d) return false;
    if (a.key === 'float' && a.level === 'warn') return d.provider.canOrder !== false && d.provider.error === null && d.provider.balanceSun !== null;
    if (a.key === 'spend' && a.level === 'warn') return !spendCapped;
    if (a.key === 'desk' && fresh?.deskStopped === true) return a.level === 'critical' || fresh.sincePass === null;
    return false;
  };
  const alarms: EnergyAlarm[] = d ? d.alarms.filter((a) => a.level !== 'ok' && !coveredElsewhere(a)) : [];
  // What is on the "Needs you" card, in the order he should meet it: what is red first (an order that may have been charged, a
  // critical alarm), then what is amber.
  const lostRed = o.lost.filter((l) => l.checkedAtMs === null);
  const lostAmber = o.lost.filter((l) => l.checkedAtMs !== null);
  const foundRental = (orderKey: string) => d?.rentals.find((r) => r.id === o.found.find((x) => x.key === orderKey)?.rentalId);
  // An order found on the desk is amber only when the desk's record says what became of it (outcomeKnown) — or it has left the list,
  // which keeps every rental still unsettled. A record that still says "no answer" or "sending" is red: it may have been charged.
  // And it is said once: when the desk's own "no answer" alarm already names that rental, that line says it, with its Settle link.
  const noAnswer = alarms.find((a) => a.key === 'unconfirmed');
  const unresolved = (key: string): boolean => { const r = foundRental(key); return r !== undefined && !outcomeKnown(r.state); };
  const foundRed = o.found.filter((x) => unresolved(x.key) && !(noAnswer !== undefined && alarmNamesRental(noAnswer, x.rentalId)));
  const foundAmber = o.found.filter((x) => !unresolved(x.key));
  const critical = alarms.filter((a) => a.level === 'critical');
  const warnings = alarms.filter((a) => a.level !== 'critical');
  /**
   * An alarm as ONE line. What it is about is what the desk says (`walletIds` on the alarm — the status answer carries them),
   * resolved against the wallet records; a desk that does not say gives the sentence alone (lib/energyOrder.ts alarmAbout, alarmLead).
   */
  const leadOf = (a: EnergyAlarm) => alarmLead(a.key, a.text, alarmAbout(a, namedWallets(d)));
  // The rental an alarm NAMES — not the first one that has buttons — by the desk's own `rentalIds`. No such rental on the list, no link.
  const alarmTarget = (a: EnergyAlarm): number | null => (d !== undefined && a.key === 'unconfirmed' ? settleTarget(a.text, d.rentals, a.rentalIds) : null);
  const jumpWord = (id: number): string => jumpWordOf(d, canChange, id);
  // The rentals a link on this card jumps to are ALWAYS in the list below, however far down the newest-five cut they would be:
  // a link to a row that is not on the page does nothing (Astra, 23 Oct 2026).
  const linked = new Set<number>([...foundRed.map((x) => x.rentalId), ...alarms.flatMap((a) => { const t = alarmTarget(a); return t === null ? [] : [t]; })]);
  return { labelOf, level, alarms, lostRed, lostAmber, foundRental, foundRed, foundAmber, critical, warnings, leadOf, alarmTarget, jumpWord, linked };
}
export type Needs = ReturnType<typeof useNeeds>;
/**
 * The rentals an alarm names that are that wallet's, and listed — and so whether the alarm concerns the wallet at all: by the
 * desk's walletIds where it says them, else by the wallet of each rental it names. The desk's "no answer" alarm names its orders,
 * never their wallets (energy/src/alarm.mjs 'unconfirmed': rentalIds only) — a wallet's sheet that read walletIds alone missed it.
 */
export function alarmRentalsOf(a: EnergyAlarm, d: EnergyStatus, walletId: number): number[] {
  return (alarmRentalIds(a) ?? []).filter((id) => d.rentals.some((r) => r.id === id && r.walletId === walletId));
}
export const alarmConcernsWallet = (a: EnergyAlarm, d: EnergyStatus, walletId: number): boolean =>
  (alarmAbout(a, namedWallets(d))?.wallets.some((w) => w.id === walletId) ?? false) || alarmRentalsOf(a, d, walletId).length > 0;
/** What a link to a rental says: "Settle" only when that rental has its settle buttons for this viewer — otherwise "See". */
export const jumpWordOf = (d: EnergyStatus | undefined, canChange: boolean, id: number): string =>
  (canChange && d?.rentals.find((r) => r.id === id)?.settleable === true ? 'Settle' : 'See');

/** The TronSave balance's tone: red at the desk's critical line, amber at its warning line — or when the balance could not be read. */
export const floatTone = (d: EnergyStatus, n: Pick<Needs, 'level'>): 'bad' | 'warn' | undefined =>
  (n.level('float') === 'critical' ? 'bad' : n.level('float') === 'warn' || d.provider.error ? 'warn' : undefined);

/** The desk's own figures beside the balance: what one transfer costs now (or why no price is shown), and the energy rented. */
export function deskFigures(d: EnergyStatus | undefined) {
  const oneTransfer = d?.prices?.rows.find((r) => r.purpose === 'top_up' && r.costSun !== null) ?? null;
  // No price to show is said with its reason — a dash alone reads as "nothing to report".
  const noPrice = oneTransfer ? null : (d?.prices?.rows.find((r) => r.purpose === 'top_up' && r.problem !== null)?.problem ?? null);
  const rentedNow = d ? d.wallets.reduce((sum, w) => sum + Number(w.rentedEnergy), 0) : 0;
  const oneTransferLabel = oneTransfer ? `${Number(oneTransfer.energy) === Number(SEND_ENERGY) ? '1 transfer' : `${units(oneTransfer.energy)} energy`} for ${lasts(oneTransfer.durationSec)}` : 'One transfer';
  return { oneTransfer, noPrice, rentedNow, oneTransferLabel };
}

// ── Is there a picture, and is it current? ──────────────────────────────────
/** The frame a screen gives a line that must be seen on a slab of its own: today's tinted slab, or the rebuilt console's box. */
export interface Slabs { bad: string; warn: string; neutral: string }
export const SLABS: Slabs = { bad: SLAB_BAD, warn: SLAB_WARN, neutral: '' };
/** No slab: the line is drawn where it stands (today's screen draws these lines inside a card of their own). */
const NO_SLABS: Slabs = { bad: '', warn: '', neutral: '' };
const OnSlab =({ className, children }: { className: string; children: ReactNode }) => (className ? <div className={className}>{children}</div> : <>{children}</>);

/**
 * Is this device offline? The read client's word — a read it has paused — and the BROWSER's: the read client starts every page
 * "online" and only listens for the browser's events, and it can miss the first one. Measured on 27 Oct 2026: a page opened
 * offline read `online: true` from the client and `navigator.onLine === false` from the browser, and its read failed twice.
 */
export const deviceOffline = (q: { fetchStatus: string }): boolean =>
  q.fetchStatus === 'paused' || !onlineManager.isOnline() || (typeof navigator !== 'undefined' && navigator.onLine === false);

/**
 * THE FIRST READ, while there is no picture (design system Appendix B, lib/read-state.ts): a read that has not completed is
 * WAITING — never "couldn't read", never "empty". 'reading': asked, no answer yet (a first read, the retry of a failure).
 * 'offline': waiting for a connection — paused, or failed only because this device has none (a connection error while the
 * browser says it is offline). 'failed': the desk, the Books or the network between them said no. 'known': there is a picture
 * (whether it is current is the lines' business: a failed refresh keeps it, said in amber, then red once it is old).
 */
export type FirstRead = 'known' | 'reading' | 'offline' | 'failed';
export function firstRead(q: { isError: boolean; isSuccess: boolean; data?: unknown; error: unknown; fetchStatus: string }): FirstRead {
  if (q.data !== undefined) return 'known';
  if (readState(q) === 'failed') return q.error instanceof TypeError && deviceOffline(q) ? 'offline' : 'failed';
  return deviceOffline(q) ? 'offline' : 'reading';
}

/**
 * THE READ ITSELF, before anything else — ONE line each, the read that failed behind its tap. No picture: red with the way out
 * when the read failed (never an empty screen), "Reading…" while it is on its way, a grey "Offline — waiting" while there is no
 * connection. A picture: red when it is old (nothing below is current), amber when a refresh failed a moment ago, red when the
 * desk has stopped making its passes. Today's screen and the rebuilt one draw these lines, and only these. `reading`: what a
 * first read on its way draws — the line, or nothing where the screen draws its own grey rows.
 */
export function ReadLines({ desk, tryAgain, slabs = SLABS, reading = <Pending what="Reading the energy desk…" /> }: {
  desk: Pick<Desk, 'q' | 'd' | 'fresh'>; tryAgain: ReactNode; slabs?: Slabs; reading?: ReactNode;
}) {
  const { q, d, fresh } = desk;
  const first = firstRead(q);
  return (
    <>
      {/* Nothing to show and the read failed: red, with the way out — never an empty screen. */}
      {first === 'failed' && (
        <OnSlab className={slabs.bad}>
          <StateLine tone="bad" more={errorWords(q.error)} action={tryAgain}>Couldn&apos;t read the energy desk.</StateLine>
        </OnSlab>
      )}
      {first === 'reading' && reading}
      {/* Waiting for a connection is not a failure: the desk is read as soon as the device is back online. */}
      {first === 'offline' && (
        <OnSlab className={slabs.neutral}>
          <StateLine tone="neutral" more="This device is offline. The energy desk is read as soon as it is back.">Offline — waiting to read the energy desk.</StateLine>
        </OnSlab>
      )}
      {/* Before anything else: are these figures current? ONE red line when they are not — the read that failed is behind its tap. */}
      {d && fresh?.held && (
        <OnSlab className={slabs.bad}>
          <StateLine tone="bad" action={tryAgain}
            more={
              <>
                {/* Offline, the polling does not fail — it waits, and often has not even tried: so the browser is asked, too. */}
                <p>{deviceOffline(q)
                  ? 'This device is offline, so the screen cannot read the energy desk.'
                  : 'The screen has not been able to read the energy desk since.'}</p>
                {q.isError && <p>{errorWords(q.error)}</p>}
              </>
            }>
            {fresh.heldFor === null ? 'This picture is of unknown age — nothing below is current.' : `This picture is ${span(fresh.heldFor)} old — nothing below is current.`}
          </StateLine>
        </OnSlab>
      )}
      {/* A read that failed a moment ago, the picture not yet old: amber. (Once it is old, the red line above says it.) */}
      {d && q.isError && !fresh?.held && (
        <OnSlab className={slabs.warn}>
          <StateLine tone="warn" more={errorWords(q.error)} action={tryAgain}>Couldn&apos;t read the energy desk just now.</StateLine>
        </OnSlab>
      )}
      {d && fresh?.deskStopped && (
        <OnSlab className={slabs.bad}>
          <StateLine tone="bad"
            more={
              <p>
                {fresh.sincePass === null
                  ? 'It has not completed a pass since it started.'
                  : Number.isFinite(fresh.sincePass)
                    ? `Its last pass was ${span(fresh.sincePass)} before this was read.`
                    : 'When it last completed a pass cannot be told.'}{' '}
                Everything below is as it was then.
              </p>
            }>
            The energy desk has stopped — nothing is renewed or read.
          </StateLine>
        </OnSlab>
      )}
    </>
  );
}

/**
 * WHAT NEEDS HIM THAT IS NOT A ROW, one line each, red first: an order this screen sent and got no answer to (red until the desk's
 * list was read since), a critical alarm, an order found on the desk whose record does not say what became of it, then the amber
 * ones. The screen says what it has SEEN of the desk's list since an order got no answer; it does not decide that an order was not
 * placed (lib/energyOrder.ts, PART THREE). Each screen gives the lines their buttons — `open` a wallet's panel or page, `rental` a
 * link to the rental a line NAMES, `noted` — and `item` their seat; `rentalsHere` says whether the rentals are listed below.
 */
export function NeedsLines({ d, needs, critical = needs.critical, warnings = needs.warnings, rentalsHere, open, rental, noted, item = (key, node) => <Fragment key={key}>{node}</Fragment>, slabs = NO_SLABS }: {
  d: EnergyStatus;
  needs: Needs;
  /** The alarms said as lines here — all of them by default; a screen whose rows carry some passes the rest. */
  critical?: EnergyAlarm[];
  warnings?: EnergyAlarm[];
  rentalsHere: boolean;
  open: (walletId: number, purpose: string) => ReactNode;
  rental: (id: number) => ReactNode;
  noted: (key: string) => ReactNode;
  item?: (key: string, node: ReactNode) => ReactNode;
  /** The frame each line is given: none (today's screen draws them in a card of their own), or the rebuilt console's box. */
  slabs?: Slabs;
}) {
  const { labelOf, lostRed, lostAmber, foundRental, foundRed, foundAmber, leadOf, alarmTarget } = needs;
  const put = (key: string, tone: 'bad' | 'warn', node: ReactNode) => item(key, <OnSlab className={slabs[tone]}>{node}</OnSlab>);
  return (
    <>
      {lostRed.map((l) => {
        const what = l.purpose === 'standing' ? 'standing order' : 'rental';
        return put(l.key, 'bad', (
          <StateLine tone="bad"
            more={
              <>
                <p>At {fmtStamp(new Date(l.failedAtMs).toISOString())} the {what} for {labelOf(l.walletId)} went out and got no usable answer. It may have been placed and charged.</p>
                <p>The screen has not read the energy desk since.</p>
              </>
            }
            action={open(l.walletId, l.purpose)}>
            No answer for {lineName(labelOf(l.walletId))} — may have been charged.
          </StateLine>
        ));
      })}
      {critical.map((a) => {
        const { lead, folded } = leadOf(a);
        const target = alarmTarget(a);
        return put(`alarm-${a.key}`, 'bad', (
          <StateLine tone="bad" more={folded ? <p>{a.text}</p> : undefined}
            action={target !== null ? rental(target) : undefined}>
            {lead}
          </StateLine>
        ));
      })}
      {foundRed.map((x) => put(x.key, 'bad', (
        <StateLine tone="bad" more={<><p>The order for {labelOf(x.walletId)} got no answer when it was sent; the desk&apos;s list now shows it as rental #{x.rentalId}.</p>{rentalsHere && <p>It is in the list below.</p>}</>}
          action={<>{rental(x.rentalId)}{noted(x.key)}</>}>
          Your order is on the desk — #{x.rentalId}, {stateIn(foundRental(x.key)?.state ?? '')}.
        </StateLine>
      )))}
      {lostAmber.map((l) => {
        const what = l.purpose === 'standing' ? 'standing order' : 'rental';
        return put(l.key, 'warn', (
          <StateLine tone="warn"
            more={
              <>
                <p>At {fmtStamp(new Date(l.failedAtMs).toISOString())} the {what} for {labelOf(l.walletId)} went out and got no usable answer.</p>
                <p>An order that reaches the desk is written down there before anything is bought — and the desk&apos;s list, read {span(((l.checkedAtMs ?? l.failedAtMs) - l.failedAtMs) / 1000)} after that, has nothing under this one.</p>
              </>
            }
            action={<>{open(l.walletId, l.purpose)}{noted(l.key)}</>}>
            No answer for {lineName(labelOf(l.walletId))} — not listed yet.
          </StateLine>
        ));
      })}
      {foundAmber.map((x) => {
        // What state it is in is read from the list being shown — never from a copy made when it was found.
        const listed = foundRental(x.key);
        return put(x.key, 'warn', (
          <StateLine tone="warn"
            more={<><p>The order for {labelOf(x.walletId)} got no answer when it was sent; the desk&apos;s list now shows it as rental #{x.rentalId}.</p>{rentalsHere
              ? <p>{listed ? 'It is in the list below.' : 'It is no longer among the rentals listed below.'}</p>
              : !listed && <p>It is no longer among the rentals listed.</p>}</>}
            action={noted(x.key)}>
            Your order is on the desk — #{x.rentalId}{listed ? <>, {stateIn(listed.state)}</> : null}.
          </StateLine>
        ));
      })}
      {warnings.map((a) => {
        const { lead, folded } = leadOf(a);
        return put(`alarm-${a.key}`, 'warn', <StateLine tone="warn" more={folded ? <p>{a.text}</p> : undefined}>{lead}</StateLine>);
      })}
    </>
  );
}

export function Energy({ initialOpen = null }: { initialOpen?: { walletId: number; kind: 'topup' | 'edit' } | null } = {}) {
  const desk = useDesk();
  const { q, d, canChange, nowMs, fresh, refresh } = desk;
  const [open, setOpen] = useState<{ walletId: number; kind: 'topup' | 'edit' } | null>(initialOpen);
  const [adding, setAdding] = useState(false);

  const toggle = (walletId: number, kind: 'topup' | 'edit') =>
    setOpen((o) => (o && o.walletId === walletId && o.kind === kind ? null : { walletId, kind }));
  /** Open a wallet's panel from a line at the top, and bring it into view. */
  const reveal = (walletId: number, kind: 'topup' | 'edit') => {
    setOpen({ walletId, kind });
    window.setTimeout(() => showMe(`[data-wallet="${walletId}"]`), 80);
  };

  const tryAgain = <button type="button" className="btn-link" onClick={refresh}>Try again</button>;
  const registerDown = d !== undefined && registerUnreadable(d);

  const needs = useNeeds(desk);
  const { level, lostRed, lostAmber, foundRed, foundAmber, critical, warnings, jumpWord, linked } = needs;
  const anyRed = lostRed.length + critical.length + foundRed.length > 0;
  const anyNeed = anyRed || lostAmber.length + foundAmber.length + warnings.length > 0;

  const { oneTransfer, noPrice, rentedNow, oneTransferLabel } = deskFigures(d);

  // The wallets table's figure columns, as wide as their widest figure (see colPx).
  const wc = d && {
    energy: colPx(d.wallets.map((w) => units(w.available)), d.wallets.map((w) => `of ${units(w.energyLimit)}`), 160),
    trx: colPx(d.wallets.map((w) => trx(w.trxBalanceSun)), [], 80),
    standing: colPx(d.wallets.map((w) => units(w.standingCovered)), d.wallets.map((w) => `of ${units(w.standingEnergy)}`), 104),
    rented: colPx(d.wallets.map((w) => units(w.rentedEnergy)), ['ending now'], 104),
  };
  const noted = (key: string) => <button type="button" className="btn-link" onClick={() => orders.dispatch({ type: 'dismiss', key })}>Noted</button>;
  /** The seat of one line on the "Needs you" card. */
  const NEED = 'py-1.5 first:pt-0 last:pb-0';

  return (
    <div className="space-y-4">
      <ScreenHeader
        title={
          <span className="inline-flex items-center gap-2">
            Energy
            {d?.network === 'nile' && <Pill tone="warn" className="align-middle">Test network</Pill>}
            <button type="button" className="-my-3.5 p-3.5 text-tg-hint hover:text-tg-text disabled:opacity-50 lg:hidden" disabled={q.isFetching} title="Refresh"
              onClick={refresh}>
              <RefreshCw size={16} className={q.isFetching ? 'animate-spin' : ''} />
              <span className="sr-only">Refresh</span>
            </button>
          </span>
        }
        subtitle={<>TRON energy for the wallets · {d ? readWords(d.fetched_at) : 'not read yet'}</>}
        actions={
          <button type="button" className="btn-secondary hidden text-body py-1.5 disabled:opacity-50 lg:inline-flex" disabled={q.isFetching}
            onClick={refresh}>
            {q.isFetching ? 'Reading…' : 'Refresh'}
          </button>
        }
        help={<AboutEnergy d={d} rentalsHere />}
      />

      <ReadLines desk={desk} tryAgain={tryAgain} />

      {d && (
        <>
          {/* ── NEEDS YOU: one card, a line each, red first. The screen says what it has SEEN of the desk's list since an order got
              no answer; it does not decide that an order was not placed (lib/energyOrder.ts, PART THREE). */}
          {anyNeed && (
            <Card tone={anyRed ? 'bad' : 'warn'}>
              <Label>Needs you</Label>
              <ul className="mt-1.5 divide-y divide-stroke-soft">
                <NeedsLines d={d} needs={needs} rentalsHere
                  open={(walletId, purpose) => <button type="button" className="btn-link" onClick={() => reveal(walletId, purpose === 'standing' ? 'edit' : 'topup')}>Open panel</button>}
                  rental={(id) => <ToRental id={id}>{jumpWord(id)} #{id} ↓</ToRental>}
                  noted={noted}
                  item={(key, node) => <li key={key} className={NEED}>{node}</li>} />
              </ul>
            </Card>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Stat label={d.provider.error !== null ? 'Balance · last known' : 'TronSave balance'} value={d.provider.balanceSun === null ? '—' : inTrx(trx(d.provider.balanceSun))} tone={floatTone(d, needs)} />
            <Stat label="Spent in 24h" value={<>{trx(d.spend.countedSun)} <span className="font-normal text-tg-hint">of {trx(d.spend.dailyCapSun)} TRX</span></>}
              tone={level('spend') === 'warn' ? 'warn' : undefined} />
            <Stat label={oneTransferLabel}
              value={oneTransfer ? inTrx(trx(oneTransfer.costSun)) : '—'} />
            <Stat label="Energy rented" value={units(String(rentedNow))} />
          </div>
          {/* Under the tiles, one line: where to send TRX, and — only when there is one — why no price is shown.
              (A burn price that cannot be read is an alarm above; the stamps are in About this screen.) */}
          {(d.provider.depositAddress || noPrice) && (
            <div className="space-y-0.5 text-meta text-tg-hint">
              {d.provider.depositAddress && (
                <p className="flex flex-wrap items-center gap-x-1.5">Fund the balance: send TRX to <CopyRef value={d.provider.depositAddress} label={mask(d.provider.depositAddress)} className="text-meta" /></p>
              )}
              {noPrice && !(d.burn && d.burn.error !== null) && <p>No price for one transfer — {reasonLead(noPrice).lead}</p>}
            </div>
          )}

          {/* ── Wallets ──────────────────────────────────────────────────── */}
          <section className="card p-0 overflow-hidden">
            <header className={SECTION_HEAD}>
              <h2 className="font-semibold text-body">
                Wallets
                {/* The chain readings' age, said once: the oldest of the wallets still read — a wallet whose reading failed says so on its own row. */}
                {readAt(d) && <span className="ml-2 text-meta font-normal text-tg-hint" title={`The wallets still read (not retired, not failing) — the oldest reading, ${fmtStamp(readAt(d) as string)}`}>{readWords(readAt(d) as string)}</span>}
              </h2>
              {canChange && (
                <button type="button" className={SMALL_BTN} disabled={registerDown && !adding} title={registerDown ? 'The Books\' register cannot be read just now' : undefined}
                  onClick={() => setAdding((v) => !v)}>
                  {adding ? 'Close' : 'Add a wallet'}
                </button>
              )}
            </header>
            {registerDown && (
              <div className="px-3 py-2 border-b border-stroke-soft">
                <StateLine tone="warn"
                  more="The Books' wallet register could not be read just now. Until it can be, no wallet is added, topped up or given a standing figure from here.">
                  Books&apos; register unreadable — top-ups and standing figures are off.
                </StateLine>
              </div>
            )}
            {adding && !registerDown && <AddWallet d={d} onDone={() => setAdding(false)} />}
            {d.wallets.length === 0 ? (
              <EmptyState>No wallets yet.</EmptyState>
            ) : (
              <>
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full table-fixed text-body">
                    {/* The figures sit beside the name, and the slack is the last column — as on every ledger. */}
                    <colgroup>
                      <col className="w-[24%]" />
                      <col style={{ width: wc?.energy }} />
                      <col style={{ width: wc?.trx }} />
                      <col style={{ width: wc?.standing }} />
                      <col style={{ width: wc?.rented }} />
                      <col className="w-[8.5rem]" />
                      <col />
                    </colgroup>
                    <thead className="text-label uppercase tracking-wide text-tg-hint">
                      <tr>
                        <th className={`text-left ${TH}`}>Wallet</th>
                        <th className={`text-right ${TH}`}>Energy</th>
                        <th className={`text-right ${TH}`}>TRX</th>
                        <th className={`text-left ${TH}`}>Standing</th>
                        <th className={`text-left ${TH}`}>Rented</th>
                        <th className={`text-right ${TH}`}></th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.wallets.map((w) => (
                        <WalletRow key={w.id} w={w} d={d} canChange={canChange} held={fresh?.held === true} nowMs={nowMs} open={open?.walletId === w.id ? open.kind : null}
                          onToggle={(kind) => toggle(w.id, kind)} onClose={() => setOpen(null)} />
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="lg:hidden">
                  {d.wallets.map((w) => (
                    <WalletCard key={w.id} w={w} d={d} canChange={canChange} held={fresh?.held === true} nowMs={nowMs} open={open?.walletId === w.id ? open.kind : null}
                      onToggle={(kind) => toggle(w.id, kind)} onClose={() => setOpen(null)} />
                  ))}
                </div>
              </>
            )}
          </section>

          <Rentals d={d} canChange={canChange} pinned={linked} />
        </>
      )}
    </div>
  );
}

/** The wallet a rental was for, by its label — the address only if the wallet is no longer listed. */
export function walletName(d: EnergyStatus, r: EnergyRental): string {
  const w = d.wallets.find((x) => x.id === r.walletId);
  return w ? nameOf(d, w) : mask(r.receiver);
}

// ── Rentals ─────────────────────────────────────────────────────────────────
/** How many rentals that need nobody show, newest first, before "All rentals". */
const RECENT = 5;

/**
 * The rentals: what needs him first — an order with no answer, one the provider took and has not filled, a delivery the wallet
 * has not shown — and every such row stays, however many there are (lib/energyOrder.ts rentalNeedsHim); so does every rental
 * a link on the "Needs you" card jumps to (`pinned`); then the newest five of the rest; the others behind "All rentals". The desk
 * lists the newest 40 and every one still unsettled.
 */
function Rentals({ d, canChange, pinned }: { d: EnergyStatus; canChange: boolean; pinned: ReadonlySet<number> }) {
  const [all, setAll] = useState(false);
  const first = (r: EnergyRental): boolean => rentalNeedsHim(r) || pinned.has(r.id);
  const needs = d.rentals.filter(first);
  const rest = d.rentals.filter((r) => !first(r));
  const shown = all ? rest : rest.slice(0, RECENT);
  const rows = [...needs, ...shown];
  const split = needs.length > 0 && shown.length > 0;
  // The Note column is always there — it is the slack that keeps the figures beside the names — and says so only when a row has one.
  const withNotes = rows.some(hasNote);
  const energyW = colPx(rows.map((r) => units(r.energy)), rows.map((r) => lasts(r.durationSec)), 96);
  const trxW = colPx(rows.map((r) => rentalTrx(r).text), [], 88);
  return (
    <section className="card p-0 overflow-hidden">
      <header className={SECTION_HEAD}>
        <h2 className="font-semibold text-body">Rentals</h2>
        {rest.length > RECENT && (
          <button type="button" className="btn-link max-lg:min-h-11" onClick={() => setAll((v) => !v)}>{all ? 'Fewer' : `All rentals (${d.rentals.length})`}</button>
        )}
      </header>
      {d.rentals.length === 0 ? (
        <EmptyState>Nothing rented yet.</EmptyState>
      ) : (
        <>
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full table-fixed text-body">
              <colgroup>
                <col className="w-[5rem]" />
                <col className="w-[22%]" />
                <col style={{ width: energyW }} />
                <col style={{ width: trxW }} />
                <col className="w-[7.5rem]" />
                <col />
              </colgroup>
              <thead className="text-label uppercase tracking-wide text-tg-hint">
                <tr>
                  <th className={`text-left ${TH}`}>When</th>
                  <th className={`text-left ${TH}`}>Wallet</th>
                  <th className={`text-right ${TH}`}>Energy</th>
                  <th className={`text-right ${TH}`}>TRX</th>
                  <th className={`text-left ${TH}`}>State</th>
                  <th className={`text-left ${TH}`}>{withNotes ? 'Note' : ''}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <RentalRows key={r.id} r={r} d={d} canChange={canChange}
                    label={split && (i === 0 || i === needs.length) ? (i === 0 ? 'Needs you' : 'Recent') : null} />
                ))}
              </tbody>
            </table>
          </div>
          {/* The phone: state · wallet, the cost at the right; then when · energy; the note beneath. */}
          <div className="lg:hidden">
            {rows.map((r, i) => (
              <RentalCard key={r.id} r={r} d={d} canChange={canChange}
                label={split && (i === 0 || i === needs.length) ? (i === 0 ? 'Needs you' : 'Recent') : null} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/** A table row of a rental — and, before the first row of a group, the group's label. */
function RentalRows({ r, d, canChange, label }: { r: EnergyRental; d: EnergyStatus; canChange: boolean; label: string | null }) {
  const cost = rentalTrx(r);
  return (
    <>
      {label && <tr className="border-t border-stroke-soft"><td colSpan={6} className="px-3 pt-2 pb-0.5"><Label>{label}</Label></td></tr>}
      <tr className="border-t border-stroke-soft" data-rental={r.id}>
        <td className={`${TD} whitespace-nowrap text-tg-hint`} title={fmtStamp(r.createdAt)}>{fmtRelative(r.createdAt)}</td>
        <td className={TD}>
          <div className="break-words">{walletName(d, r)}</div>
          <div className="text-meta text-tg-hint">{r.purpose === 'standing' ? 'standing' : 'rental'}</div>
        </td>
        <td className={`${TD} text-right font-mono tabular`}>
          {units(r.energy)}
          <div className="font-sans text-meta text-tg-hint">{lasts(r.durationSec)}</div>
        </td>
        <td className={`${TD} text-right font-mono tabular whitespace-nowrap`} title={cost.tip}>{cost.text}</td>
        <td className={TD}><RentalState r={r} asOf={d.asOf} /></td>
        <td className={TD}><RentalNote r={r} canChange={canChange} /></td>
      </tr>
    </>
  );
}

function RentalCard({ r, d, canChange, label }: { r: EnergyRental; d: EnergyStatus; canChange: boolean; label: string | null }) {
  const cost = rentalTrx(r);
  return (
    <>
      {label && <div className="px-3 pt-2"><Label>{label}</Label></div>}
      <div className={CARD} data-rental={r.id}>
        <div className={LEDGER.cardLine1}>
          <Pill tone={STATE_TONE[r.state]} className="shrink-0">{STATE_WORD[r.state]}</Pill>
          <span className="flex-1 min-w-0 break-words">
            {walletName(d, r)}
            <span className="text-tg-hint"> · {r.purpose === 'standing' ? 'standing' : 'rental'}</span>
          </span>
        </div>
        <div className={CARD_LINE2}>
          <span className={LEDGER.whenText} title={fmtStamp(r.createdAt)}>{fmtRelative(r.createdAt)}</span>
          <span className="font-mono tabular text-meta text-tg-hint">{units(r.energy)} energy · {lasts(r.durationSec)}</span>
          {r.state === 'delegated' && r.expiresAt && <span className="text-tg-hint">{endsIn(secondsUntil(r.expiresAt, d.asOf))}</span>}
          <span className="amt ml-auto text-body" title={cost.tip}>{cost.text} <span className="text-tg-hint">TRX</span></span>
        </div>
        <RentalNote r={r} canChange={canChange} />
      </div>
    </>
  );
}

function RentalState({ r, asOf }: { r: EnergyRental; asOf: string }) {
  return (
    <>
      <Pill tone={STATE_TONE[r.state]}>{STATE_WORD[r.state]}</Pill>
      {r.state === 'delegated' && r.expiresAt && <div className="mt-0.5 whitespace-nowrap text-meta text-tg-hint">{endsIn(secondsUntil(r.expiresAt, asOf))}</div>}
    </>
  );
}

/** What the desk recorded against a rental: its note's first clause (the whole of it behind its tap), and a delivery the wallet has not shown. */
export function RentalNoteLines({ r, slabs = NO_SLABS }: { r: EnergyRental; slabs?: Slabs }) {
  const unseen = r.state === 'delegated' && r.chainSeenAt === null;
  const note = r.detail ? noteLead(r.detail) : null;
  return (
    <>
      {r.detail && note && <StateLine tone="neutral" more={note.folded ? r.detail : undefined}>{capital(note.lead)}</StateLine>}
      {unseen && <OnSlab className={slabs.warn}><StateLine tone="warn">Provider says delivered — not on the wallet yet.</StateLine></OnSlab>}
    </>
  );
}

/** The label and its buttons: on the line (today's row), or beneath it (`stacked`, the rebuilt sheet). */
function SettleRow({ stacked, label, buttons }: { stacked: boolean; label: ReactNode; buttons: ReactNode }) {
  return stacked ? (
    <div className="space-y-2">
      <StateLine tone="neutral">{label}</StateLine>
      <div className="flex flex-wrap gap-2">{buttons}</div>
    </div>
  ) : (
    <StateLine tone="neutral" action={<span className="inline-flex flex-wrap gap-1.5">{buttons}</span>}>{label}</StateLine>
  );
}

/**
 * SETTLING A RENTAL the desk cannot settle itself — one copy, for today's row and the rebuilt rental sheet. An order the provider
 * ACCEPTED was paid for when it was placed: the desk takes no "not charged" for it, so that button is not offered. For an order with
 * no answer the desk checks the provider's list itself. Each press is confirmed, with what it does; a read-only admin is told that
 * settling needs full access; what the desk said back is the line under it.
 */
export function SettleLines({ r, canChange, stacked = false, btn = 'btn-secondary text-meta py-1 px-2 max-lg:min-h-11 max-lg:px-3' }: {
  r: EnergyRental; canChange: boolean; stacked?: boolean; btn?: string;
}) {
  const qc = useQueryClient();
  const settle = useMutation({
    mutationFn: (charged: boolean) => api.energySettle(r.id, { charged }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['energy'] }),
  });
  const ask = (charged: boolean) => {
    const question = charged
      ? `Settle rental ${r.id} as CHARGED? It stays counted in that day's spend.`
      : `Settle rental ${r.id} as NOT charged? It stops counting. The desk checks the provider's order list itself first, and says no if anything there could be this order.`;
    if (confirm(question)) settle.mutate(charged);
  };
  return (
    <>
      {r.settleable && (
        canChange ? (
          // An order the provider ACCEPTED was paid for when it was placed: the desk takes no "not charged" for it,
          // so that button is not offered. For an order with no answer the desk checks the provider's list itself.
          r.state === 'unconfirmed' ? (
            <SettleRow stacked={stacked} label={<>Check the provider&apos;s order list, then:</>}
              buttons={
                <>
                  <button type="button" className={btn} disabled={settle.isPending} onClick={() => ask(false)}>Not charged</button>
                  <button type="button" className={btn} disabled={settle.isPending} onClick={() => ask(true)}>Charged</button>
                </>
              } />
          ) : (
            <SettleRow stacked={stacked} label="The provider took it and has not delivered it:"
              buttons={<button type="button" className={btn} disabled={settle.isPending} onClick={() => ask(true)}>Close it — counted as spent</button>} />
          )
        ) : <StateLine tone="neutral">Settling it needs full access.</StateLine>
      )}
      {settle.isError && <Said text={errorWords(settle.error)} />}
      {settle.data && !settle.data.ok && <Said text={settle.data.reason ?? 'The desk gave no reason.'} />}
    </>
  );
}

/** What the desk recorded against a rental, and — for one it cannot settle itself — the two buttons that do. */
function RentalNote({ r, canChange }: { r: EnergyRental; canChange: boolean }) {
  if (!hasNote(r)) return null;
  return (
    <div className="space-y-1 max-lg:mt-1">
      <RentalNoteLines r={r} />
      <SettleLines r={r} canChange={canChange} />
    </div>
  );
}

interface WalletProps {
  w: EnergyWallet;
  d: EnergyStatus;
  canChange: boolean;
  open: 'topup' | 'edit' | null;
  /** The picture on screen is old: a price shown in a panel is then not a current one. */
  held: boolean;
  /** The viewer's ticking clock — a quote has its own age, whatever the polling says (lib/energyOrder.ts quoteStale). */
  nowMs: number;
  onToggle: (kind: 'topup' | 'edit') => void;
  onClose: () => void;
}

/** The oldest chain reading of the wallets still read and reading well — said once, above the list. null when none has been read. */
export function readAt(d: EnergyStatus): string | null {
  const seen = d.wallets.filter((w) => w.status !== 'retired' && w.observedAt !== null && !w.observeError).map((w) => w.observedAt as string);
  return seen.length === 0 ? null : seen.reduce((a, b) => (Date.parse(b) < Date.parse(a) ? b : a));
}

/** An activation still in progress: asked for, and the chain not yet showing the account (desk db/005). */
export const activating = (w: EnergyWallet): boolean =>
  w.activation != null && (w.activation.state === 'sending' || w.activation.state === 'requested');
/** Less than one transfer's energy on a wallet that is doing its work: its next send burns TRX. (A paused wallet is not doing any.) */
export const lowOnEnergy = (w: EnergyWallet): boolean => w.status === 'active' && w.available !== null && Number(w.available) < Number(SEND_ENERGY);
/** Where a wallet is in its life on the desk — paused or retired — as a tag of its own, never words in its name; an active one has none. */
export function walletLifecycle(w: EnergyWallet): { word: string; tone: PillTone } | null {
  if (w.status === 'retired') return { word: 'Retired', tone: 'neutral' };
  if (w.status === 'paused') return { word: 'Paused', tone: 'warn' };
  return null;
}

/**
 * A wallet's energy as the chain has it. A reading that FAILED is said as that — amber, with how long ago — and the last
 * figure is not drawn as current beside it (it was "0 of 0 !", which reads as a reading). An activation the desk asked for
 * and got no answer to is red: it may have been made, and charged.
 */
export function WalletEnergy({ w, inline = false }: { w: EnergyWallet; inline?: boolean }) {
  // Retired, the desk no longer reads it: its figures are old, and the stamp above the list is not theirs.
  if (w.status === 'retired') {
    return <span className="font-sans text-meta text-tg-hint" title={w.observedAt ? fmtStamp(w.observedAt) : undefined}>{w.observedAt ? `last read ${fmtRelative(w.observedAt)}` : 'not read'}</span>;
  }
  if (w.observeError) {
    const ago = w.observeErrorAt ? ` ${fmtRelative(w.observeErrorAt)}` : '';
    const last = w.available !== null ? ` Last reading: ${units(w.available)} of ${units(w.energyLimit)}${w.observedAt ? `, ${fmtRelative(w.observedAt)}` : ''}.` : '';
    return <span className="font-sans text-meta text-warn" title={`The chain could not be read${ago}: ${w.observeError}.${last}`}>read failed{ago}</span>;
  }
  if (w.activated === false) {
    const a = w.activation?.state;
    if (a === 'unanswered') return <span className="font-sans text-meta text-neg">activation: no answer</span>;
    if (a === 'refused') return <span className="font-sans text-meta text-warn">activation refused</span>;
    if (a === 'expired') return <span className="font-sans text-meta text-warn">activation expired</span>;
    return <span className="font-sans text-meta text-warn">{activating(w) ? 'being activated' : 'not on-chain yet'}</span>;
  }
  if (w.available === null) return <span className="font-sans text-meta text-tg-hint">not read yet</span>;
  const low = lowOnEnergy(w);
  const short = w.energyLimit !== null && Number(w.energyLimit) > 0 && Number(w.available) < Number(w.energyLimit);
  const figure = <span className={low ? 'text-warn' : ''} title={low ? `Less than one transfer needs (${units(String(SEND_ENERGY))}): the next send burns TRX.` : undefined}>{units(w.available)}</span>;
  if (!short) return figure;
  return inline
    ? <>{figure}<span className="text-tg-hint"> of {units(w.energyLimit)}</span></>
    : <>{figure}<div className="font-sans text-meta text-tg-hint">of {units(w.energyLimit)}</div></>;
}
/** A wallet's state as ONE tag, and whether its row goes above the calm ones (it decides the order, never what may be done). */
export interface Seat { word: string; tone: PillTone; needs: boolean }
/**
 * The wallet's state as ONE tag — for the rebuilt register's rows (screens/v2/energy) — in the order WalletEnergy's cell says it:
 * retired · the chain unread · an activation · an order on its way or unanswered · not read yet · less than one transfer (an
 * active wallet) · standing short · paused · the register's no · Available. Change one, change the other.
 */
export function walletSeat(d: EnergyStatus, w: EnergyWallet): Seat {
  const life = walletLifecycle(w);
  if (w.status === 'retired' && life) return { ...life, needs: false };
  if (w.observeError) return { word: 'Read failed', tone: 'warn', needs: true };
  if (w.activated === false) {
    const a = w.activation?.state;
    if (a === 'unanswered') return { word: 'Activation unanswered', tone: 'bad', needs: true };
    if (a === 'refused') return { word: 'Activation refused', tone: 'warn', needs: true };
    if (a === 'expired') return { word: 'Activation expired', tone: 'warn', needs: true };
    return { word: activating(w) ? 'Being activated' : 'Not on-chain', tone: 'warn', needs: true };
  }
  if (w.unresolved) return { word: STATE_WORD[w.unresolved.state], tone: STATE_TONE[w.unresolved.state], needs: true };
  if (w.available === null) return { word: 'Not read yet', tone: 'neutral', needs: false };
  if (lowOnEnergy(w)) return { word: 'Low energy', tone: 'warn', needs: true };
  if (Number(w.standingEnergy) > 0 && Number(w.standingCovered) < Number(w.standingEnergy)) return { word: 'Standing short', tone: 'warn', needs: true };
  if (w.status === 'paused' && life) return { ...life, needs: false };
  if (registerOf(d, w).kind !== 'live') return { word: 'Held back', tone: 'warn', needs: false };
  return { word: 'Available', tone: 'good', needs: false };
}

/** The standing figure: the figure kept — and, when less than that is in place, amber, with what it should be. */
export function WalletStanding({ w, inline = false }: { w: EnergyWallet; inline?: boolean }) {
  if (Number(w.standingEnergy) === 0) return <span className="text-tg-hint">—</span>;
  const short = Number(w.standingCovered) < Number(w.standingEnergy);
  const perDay = sendsADayOf(w.standingEnergy);
  // Why it is not in place is the "standing" alarm at the top; the transfers a day are on hover.
  return (
    <span title={perDay !== null ? `${perDay} transfer${perDay === 1 ? '' : 's'} a day` : undefined}>
      {short ? <span className="text-warn">{units(w.standingCovered)}</span> : units(w.standingEnergy)}
      {short && (inline
        ? <span className="text-tg-hint"> of {units(w.standingEnergy)}</span>
        : <div className="font-sans text-meta text-tg-hint">of {units(w.standingEnergy)}</div>)}
    </span>
  );
}
export function WalletRented({ w, asOf, inline = false }: { w: EnergyWallet; asOf: string; inline?: boolean }) {
  const rented = Number(w.rentedEnergy) > 0;
  const ends = w.nextExpiry ? endsIn(secondsUntil(w.nextExpiry, asOf)) : null;
  return (
    <>
      {rented && <>{units(w.rentedEnergy)}{ends && (inline ? <span className="text-tg-hint"> · {ends}</span> : <div className="font-sans text-meta text-tg-hint">{ends}</div>)}</>}
      {/* An order in progress says so in a word; with nothing delivered yet, the word is the whole cell. */}
      {w.unresolved && <span className={rented ? 'ml-1.5' : ''}><Pill tone={STATE_TONE[w.unresolved.state]}>{STATE_WORD[w.unresolved.state]}</Pill></span>}
      {!rented && !w.unresolved && <span className="text-tg-hint">—</span>}
    </>
  );
}
/** The wallet's name on its row: the label — whole, wrapping, never cut off (the register's name for it on hover) — and its status. */
function WalletName({ w, d }: { w: EnergyWallet; d: EnergyStatus }) {
  const s = registerOf(d, w);
  const life = walletLifecycle(w);
  return (
    <>
      <span className="font-medium break-words" title={s.kind === 'live' && s.name !== w.label ? `On the desk as ${w.label}` : undefined}>{nameOf(d, w)}</span>
      {life && <Pill tone={life.tone} className="ml-1.5 align-middle">{life.word}</Pill>}
    </>
  );
}
/**
 * The Books' register's no to a wallet, as a line of its own — visible, not on hover: it is why Top up is off, and a phone has
 * no hover (a review, 18 Oct 2026). One short line: the reason, not the verdict said over again; the whole sentence on hover.
 * Only for such a wallet; the others stay one row.
 */
export function WalletRefusal({ w, d }: { w: EnergyWallet; d: EnergyStatus }) {
  const s = registerOf(d, w);
  return s.kind === 'not_live' ? <div className="text-meta text-warn" title={holdBack(s) ?? undefined}>{registerLine(s)}</div> : null;
}
/**
 * An activation that went wrong, in words: it got no answer (and may have been made, and charged); the provider refused it (and
 * why); it was not seen on the chain in time (and is counted as spent). Null for one that is on its way, or never asked for. The
 * desk's own note is the line's tip.
 */
export function activationLine(w: EnergyWallet, d: EnergyStatus): { tone: 'bad' | 'warn'; text: string; tip: string | null } | null {
  const a = w.activated === false ? w.activation : null;
  if (!a) return null;
  if (a.state === 'unanswered') return { tone: 'bad', text: `May have been made and charged (${trx(a.costSun)} TRX).`, tip: a.detail };
  if (a.state === 'refused') {
    // "the provider refused it: the account would be below its minimum balance" — the reason, not the verdict said again; the whole on hover.
    const why = reasonLead((a.detail ?? '').replace(/^the provider refused it:\s*/i, ''), 10).lead;
    return { tone: 'warn', text: a.detail ? capital(why) : 'The provider refused it.', tip: a.detail };
  }
  if (a.state === 'expired') return { tone: 'warn', text: `Not seen within ${Math.round((d.activation?.waitSec ?? 300) / 60)} min — still counted as spent.`, tip: a.detail };
  return null;
}
/** That activation line, under the wallet's name on today's screen. */
export function WalletActivation({ w, d }: { w: EnergyWallet; d: EnergyStatus }) {
  const l = activationLine(w, d);
  return l === null ? null : <div className={`text-meta ${l.tone === 'bad' ? 'text-neg' : 'text-warn'}`} title={l.tip ?? undefined}>{l.text}</div>;
}
/** Why a NEW top-up is off for this wallet — null when it is not: never while an order that got no answer waits to be sent again (lib/energyOrder.ts). */
export function useTopUpOff(d: EnergyStatus, w: EnergyWallet): string | null {
  const pending = useOrders().topUps[w.id];
  const off = topUpHeldBack(pending, w.status !== 'active' ? `This wallet is ${w.status}` : holdBack(registerOf(d, w)));
  return off;
}

function WalletActions({ w, d, open, onToggle, canChange }: Pick<WalletProps, 'w' | 'd' | 'open' | 'onToggle' | 'canChange'>) {
  // Held back for a NEW top-up — never while an order that got no answer waits to be sent again (lib/energyOrder.ts).
  const off = useTopUpOff(d, w);
  return (
    <span className="inline-flex gap-1.5">
      {/* A title only when the button is OFF, to say why. On a live button it
          would replace the button's own word as the name a screen reader says. */}
      <button type="button" className={`${SMALL_BTN} whitespace-nowrap disabled:opacity-50`} disabled={off !== null && open !== 'topup'}
        title={off ?? undefined} onClick={() => onToggle('topup')}>
        {open === 'topup' ? 'Close' : 'Rent energy'}
      </button>
      {canChange && (
        <button type="button" className={SMALL_BTN} onClick={() => onToggle('edit')}>
          {open === 'edit' ? 'Close' : 'Edit'}
        </button>
      )}
    </span>
  );
}

/** ONE ROW: name, address and what is wrong · energy · TRX · standing · rented · actions. The forms open beneath it, and only on a press. */
function WalletRow({ w, d, canChange, open, held, nowMs, onToggle, onClose }: WalletProps) {
  return (
    <>
      <tr className="border-t border-stroke-soft" data-wallet={w.id}>
        <td className={TD}>
          <WalletName w={w} d={d} />
          <div className="mt-0.5"><Address address={w.address} network={d.network} /></div>
          <WalletRefusal w={w} d={d} />
          <WalletActivation w={w} d={d} />
        </td>
        <td className={`${TD} text-right font-mono tabular`}><WalletEnergy w={w} /></td>
        <td className={`${TD} text-right font-mono tabular whitespace-nowrap`}>{w.status === 'retired' ? <span className="text-tg-hint">—</span> : trx(w.trxBalanceSun)}</td>
        <td className={`${TD} font-mono tabular`}><WalletStanding w={w} /></td>
        <td className={`${TD} font-mono tabular`}><WalletRented w={w} asOf={d.asOf} /></td>
        <td className={`${TD} text-right`}><WalletActions w={w} d={d} open={open} onToggle={onToggle} canChange={canChange} /></td>
        <td />
      </tr>
      {open && (
        <tr className="bg-tg-secondary/30">
          <td colSpan={7} className="px-3 py-3">
            {open === 'topup' ? <TopUp w={w} d={d} canChange={canChange} held={held} nowMs={nowMs} /> : <EditWallet w={w} d={d} onDone={onClose} />}
          </td>
        </tr>
      )}
    </>
  );
}

/** The wallet on a phone: name · energy; what is wrong; then address · TRX · standing · rented, the actions at the right. */
function WalletCard({ w, d, canChange, open, held, nowMs, onToggle, onClose }: WalletProps) {
  return (
    <div className={CARD} data-wallet={w.id}>
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-2 gap-y-0.5 text-body leading-snug">
        <span className="min-w-0 flex-1 basis-40"><WalletName w={w} d={d} /></span>
        <span className="amt ml-auto text-right"><WalletEnergy w={w} inline /></span>
      </div>
      <WalletRefusal w={w} d={d} />
      <WalletActivation w={w} d={d} />
      <div className={CARD_LINE2}>
        <Address address={w.address} network={d.network} />
        <span className="amt text-tg-hint">{w.status === 'retired' || w.trxBalanceSun === null ? '—' : `${trx(w.trxBalanceSun)} TRX`}</span>
        {Number(w.standingEnergy) > 0 && <span className="text-tg-hint">standing <span className="amt text-tg-text"><WalletStanding w={w} inline /></span></span>}
        {(Number(w.rentedEnergy) > 0 || w.unresolved) && <span className="text-tg-hint">rented <span className="amt text-tg-text"><WalletRented w={w} asOf={d.asOf} inline /></span></span>}
        <span className="ml-auto"><WalletActions w={w} d={d} open={open} onToggle={onToggle} canChange={canChange} /></span>
      </div>
      {open && (
        <div className="mt-2 rounded-lg bg-tg-secondary/30 px-2 py-2">
          {open === 'topup' ? <TopUp w={w} d={d} canChange={canChange} held={held} nowMs={nowMs} /> : <EditWallet w={w} d={d} onDone={onClose} />}
        </div>
      )}
    </div>
  );
}

/**
 * A WALLET'S TOP-UP — its state, and what may be done with it: one copy, for today's panel and the rebuilt Top up page
 * (screens/v2/energy/Rent.tsx). The quote on screen IS the order.
 *
 * The panel's rules — what an edit drops, which price is kept, what may be sent and when nothing may move — are lib/energyOrder.ts,
 * pure functions with their own checks. This only feeds that state, and reads it.
 */
export function useTopUp({ w, d, canChange, held, nowMs }: { w: EnergyWallet; d: EnergyStatus; canChange: boolean; held: boolean; nowMs: number }) {
  const qc = useQueryClient();
  // This wallet's panel — from the store, not from this component: the table
  // and the cards both draw it, and closing it does not forget it.
  const all = useOrders();
  const s = all.topUps[w.id] ?? topUpInitial<EnergyPreview>();
  const dispatch = (action: TopUpAction<EnergyPreview>) => orders.dispatch({ type: 'top_up', walletId: w.id, action });
  // The store refuses a second `send` — but two clicks can land before the
  // first has been drawn. (They would carry the same key and be one order at
  // the desk; this keeps it to one request.)
  const inFlight = useRef(false);

  const price = async () => {
    if (s.sending || s.pricingAt !== null) return;
    const asked = topUpAsked(s);
    if (!topUpAskable(asked)) { dispatch({ type: 'not_priced', at: null, problem: 'Enter the energy as a whole number.' }); return; }
    const at = s.edits;
    // The term priced is the term ordered — the default too, as seconds (lib/energyOrder.ts termToSend).
    const term = termToSend(d.policy, s.term);
    dispatch({ type: 'pricing' });
    try {
      const quote = await api.energyPreview({ walletId: w.id, energy: asked, ...(term !== null ? { durationSec: term } : {}) });
      // The key is minted HERE, with the amount, the term and the price it goes with.
      dispatch({ type: 'priced', at, asked, term, key: mintKey('topup', w.id), quote, atMs: Date.now() });
    } catch (e) {
      dispatch({ type: 'not_priced', at, problem: errorWords(e) });
    }
  };

  const rent = async () => {
    const order = topUpOrder(s, w.id);          // what was PRICED — never what the form says now
    if (order === null || inFlight.current) return;
    // A term of days is many days' spending at once: said in full, and confirmed, before it goes. (Not again for
    // the same order sent after it got no answer — that was confirmed when it was first sent.)
    const days = (s.quoted?.quote.durationSec ?? 0) / 86_400;
    if (days > 1 && s.unanswered === null
      && !confirm(`Rent ${units(s.quoted?.quote.energy)} energy for ${nameOf(d, w)}, for ${lasts(s.quoted?.quote.durationSec ?? 0)}, for ${trx(order.maxCostSun)} TRX? `
        + 'It is paid now, from the TronSave balance, and counts against the limit on any 24 hours in full.')) return;
    inFlight.current = true;
    dispatch({ type: 'send' });
    try {
      // With the wallet's address: the one the Books' register is asked about, and the desk holds the order to.
      const res = await api.energyRent({ ...order, address: w.address });
      // CHECKED, not trusted: what came back counts as the desk's answer only if it is shaped like one — to THIS order.
      const outcome = orderOutcome(res, { requestKey: order.requestKey, walletId: w.id });
      if (outcome === null) dispatch({ type: 'unanswered', why: UNREADABLE_ANSWER, atMs: Date.now() });
      else dispatch({ type: 'answered', outcome, atMs: Date.now() });
    } catch (e) {
      // No answer. The order may or may not have reached the desk — and the
      // screen remembers that, by this order's key, whatever happens to this panel.
      dispatch({ type: 'unanswered', why: errorWords(e), atMs: Date.now() });
    } finally {
      inFlight.current = false;
      void qc.invalidateQueries({ queryKey: ['energy'] });
    }
  };

  const quote = s.quoted?.quote ?? null;
  const order = topUpOrder(s, w.id);
  const pricing = s.pricingAt !== null;
  // The panel can be open when the register's answer changes. A NEW order is then held back here (the desk would
  // turn it down); the same order sent again after it got no answer is not — the desk answers it with what became of
  // it (lib/energyOrder.ts topUpHeldBack).
  const hold = holdBack(registerOf(d, w));
  const heldBack = topUpHeldBack(s, w.status !== 'active' ? `This wallet is ${w.status}.` : hold);
  // The order that got no answer WAS on the desk's list when it was read again: the panel then says where it is. It is found by the
  // order's OWN key in the list on screen — and, once the rental has left that list (it is settled), by the rental the panel was told
  // of when the list showed it: that is in the quote (lib/energyOrder.ts TopUpQuoted.rentalId). Never by the screen's notice, which
  // can be put away ("Noted") and says nothing about the order (Astra, 23 Oct 2026, twice).
  const unansweredKey = s.unanswered !== null && s.quoted !== null ? s.quoted.key : null;
  const onDeskRental = unansweredKey !== null ? d.rentals.find((r) => r.requestKey === unansweredKey) : undefined;
  const onDeskId = onDeskRental?.id ?? (unansweredKey !== null ? s.quoted?.rentalId ?? undefined : undefined);
  const onDesk = onDeskId !== undefined;
  const quoted = quote !== null && quote.ok && !quote.nothingToDo;
  // Not the current price when the picture is old — AND when the quote itself is: it has its own age (priced at s.quoted.atMs),
  // and the polling being healthy says nothing about it. Display only: what may be ordered is lib/energyOrder.ts's, unchanged.
  const stale = held || (s.quoted !== null && quoteStale(s.quoted.atMs, nowMs));
  // A price on screen is dated: the quote itself, and a refusal that carries prices ("renting costs 4.371 TRX against 4.1 TRX to burn").
  const pricesShown = quoted || (quote !== null && !quote.ok && quote.costSun !== null && quote.burnCostSun !== null);
  const why = w.status !== 'active' ? `This wallet is ${w.status}` : hold ? registerLine(registerOf(d, w)) : null;
  // The order button: there is a quote to order from (or an order on its way), and the viewer may order.
  const rentShown = (order !== null || s.sending) && s.quoted !== null && quote !== null && quote.costSun !== null && canChange;
  // The order button is gold only for a current quote not already on the desk; the price button is then the quiet one.
  const rentGold = rentShown && !(stale || onDesk);
  /** The price button's word. After an order with no answer, pricing again is a NEW order — it says so. `again`: "Price again" once priced. */
  const priceWord = (again = false): string => (pricing ? 'Reading…' : s.unanswered !== null ? 'Price a new order' : again && quoted ? 'Price again' : 'Check the price');
  /** The order button's word. Its price is said as every figure is (trx); the order carries it to the sun, and is refused if it would cost more. */
  const rentWord = (costSun: string): string => (s.sending ? 'Ordering…' : s.unanswered !== null ? `Send the same order again (${trx(costSun)} TRX)` : `Rent for ${trx(costSun)} TRX`);
  return { s, dispatch, price, rent, quote, order, pricing, hold, heldBack, onDeskRental, onDeskId, onDesk, quoted, stale, pricesShown, why, rentShown, rentGold, priceWord, rentWord };
}
export type TopUp = ReturnType<typeof useTopUp>;

/** The register's or the wallet's no to a NEW top-up, as one line — the whole of it behind its tap. */
export function TopUpHeld({ t, w }: { t: TopUp; w: EnergyWallet }) {
  const { why, hold } = t;
  if (!why) return null;
  return (
    <StateLine tone="warn" more={<p>{hold ?? `This wallet is ${w.status}.`} The Books ask the desk for no new energy for it.</p>}>
      {why} — no top-ups.
    </StateLine>
  );
}

/**
 * What to rent: the energy, by transfers (an amount above the desk's largest order is shown, and cannot be chosen) or another
 * amount, and the term — the terms the desk says it takes, and only those. Any edit drops the price. Nothing moves while an order
 * is on its way: what is on the panel is that order. `amountApart`: the amount on a line of its own (the rebuilt page).
 */
export function TopUpFields({ t, d, amountApart = false }: { t: TopUp; d: EnergyStatus; amountApart?: boolean }) {
  const { s, dispatch } = t;
  const amount = s.target === 'other' && (
    <Field label="Amount" hint="whole units">
      <input className={amountApart ? 'input font-mono' : 'input'} inputMode="numeric" value={s.other} disabled={s.sending} onChange={(e) => dispatch({ type: 'edit', other: e.target.value })} />
    </Field>
  );
  return (
    <>
      <FieldRow>
        <Field label="Energy">
          {/* Not while an order is on its way: what is on the panel is that order. */}
          <select className="select" value={s.target} disabled={s.sending} onChange={(e) => dispatch({ type: 'edit', target: e.target.value as TopUpTarget })}>
            {/* An amount above the desk's largest order is shown, and cannot be chosen. */}
            {TOP_UP_CHOICES.map((c) => (
              <option key={c.target} value={c.target} disabled={BigInt(c.target) > BigInt(d.policy.maxEnergy)}>{c.text}</option>
            ))}
            <option value="other">Another amount</option>
          </select>
        </Field>
        {!amountApart && amount}
        {/* The terms the desk says it takes — and only those. Changing it drops the price, as changing the amount does. */}
        <Field label="For">
          <select className="select" value={String(s.term ?? d.policy.topUpDurationSec)} disabled={s.sending}
            onChange={(e) => dispatch({ type: 'edit', term: Number(e.target.value) })}>
            {topUpTerms(d.policy).map((t) => <option key={t} value={String(t)}>{lasts(t)}</option>)}
          </select>
        </Field>
      </FieldRow>
      {amountApart && amount}
    </>
  );
}

/**
 * WHAT BECAME OF THE ORDER THAT GOT NO ANSWER — said FIRST, before the price: it is why the order button says "the same order".
 * Found on the desk, its record still "no answer" (or "sending"): RED — finding it does not say whether the provider charged for
 * it. Found, its record saying what became of it: amber, where it is. Not found: red — it may or may not have reached the desk;
 * the button sends the SAME order again (it cannot be placed twice), and checking the price again starts a new one.
 * `rentalsHere`: the rentals are listed below (today's screen); the rebuilt page sends him to the rental on Energy (`rentalLink`).
 */
export function TopUpNoAnswer({ t, rentalsHere, rentalLink, slabs = SLABS }: { t: TopUp; rentalsHere: boolean; rentalLink?: (id: number) => ReactNode; slabs?: Slabs }) {
  const { s, onDesk, onDeskId, onDeskRental } = t;
  if (s.unanswered === null) return null;
  return onDesk ? (onDeskRental !== undefined && !outcomeKnown(onDeskRental.state) ? (
    // Found on the desk — and the desk's own record still says "no answer" (or "sending"): finding it does not say whether the
    // provider charged for it. The warning stays: red, on the line.
    <OnSlab className={slabs.bad}>
      <StateLine tone="bad"
        more={
          <>
            <p>{s.unanswered}</p>
            <p>It is on the desk as rental #{onDeskId} — {stateIn(onDeskRental.state)}. The desk does not know whether the provider charged for it.</p>
            <p>
              {onDeskRental.settleable
                ? (rentalsHere ? 'Check the provider\'s own order list, then settle it in the list below.' : 'Check the provider\'s own order list, then settle it from its rental on Energy.')
                : 'Check the provider\'s own order list. It cannot be settled here yet.'}
              {' '}Sending the same order again only asks the desk what became of it.
            </p>
          </>
        }
        action={rentalLink && onDeskId !== undefined ? rentalLink(onDeskId) : undefined}>
        May have been charged — do not order again (#{onDeskId}).
      </StateLine>
    </OnSlab>
  ) : (
    <StateLine tone="warn"
      more={<><p>{s.unanswered}</p><p>No usable answer came back (at {stamp(s.saidAtMs)}), but the desk&apos;s list shows it. Sending it again would only be told the same.</p></>}>
      On the desk — rental #{onDeskId}{onDeskRental ? <> ({stateIn(onDeskRental.state)})</> : null}.
    </StateLine>
  )) : (
    <StateLine tone="bad"
      more={
        <>
          <p>{s.unanswered}</p>
          <p>
            No usable answer came back (at {stamp(s.saidAtMs)}), so the order may or may not have reached the desk.
            The button below sends the SAME order again — it cannot be placed twice. Checking the price again starts a new order.
          </p>
        </>
      }>
      No answer at {stamp(s.saidAtMs)} — may or may not be placed.
    </StateLine>
  );
}

/** What the burn the price is weighed against is called: "every day" for a term the desk weighed day by day. */
export const burnLabel = (quote: EnergyPreview): string => (quote.lastingEnergy != null ? 'Burning every day' : 'Burning');
/** The burn the price is weighed against, and what renting saves, on one line (today's panel). */
export function BurnSaves({ quote }: { quote: EnergyPreview }) {
  return <>{burnLabel(quote)}: {trx(quote.burnCostSun)} TRX · saves {saves(quote.savingBps)}</>;
}

/** Why this amount — behind its tap: what the wallet has and is short, and what burning that would cost against renting it. */
export function WhyThisAmount({ quote }: { quote: EnergyPreview }) {
  const padded = quote.energy && quote.neededEnergy && Number(quote.energy) > Number(quote.neededEnergy);
  return (
    <MoreDetails label="Why this amount">
      <p className="text-meta text-tg-hint">
        {/* For a term of days the desk weighs a burn a day and counts what lasts the term — and says so by sending
            lastingEnergy. A desk that does not (an older one) weighed one burn, and is described as it decided. */}
        {quote.lastingEnergy != null
          ? <>It has {units(quote.lastingEnergy)} rented for the whole {lasts(quote.durationSec)} and is {units(quote.neededEnergy)} short</>
          : <>It has {units(quote.energyNow?.available)} and is {units(quote.neededEnergy)} short</>}
        {padded && <> (the smallest order is {units(quote.energy)})</>}.
        {quote.lastingEnergy != null
          ? <> Burning that every day of the {lasts(quote.durationSec)} would cost {trx(quote.burnCostSun)} TRX — renting saves {saves(quote.savingBps)}.</>
          : <> Burning that would cost {trx(quote.burnCostSun)} TRX — renting saves {saves(quote.savingBps)}.</>}
      </p>
    </MoreDetails>
  );
}

/**
 * WHAT THE DESK SAID TO A PRICE, AND TO AN ORDER, in the order he should read it: a price that could not be asked; nothing to
 * rent; the price itself (`price` — each screen lays it out); not rented, and why; when it was priced — amber once the picture OR
 * the quote itself is old; and what became of the last order (Outcome).
 */
export function TopUpAnswer({ t, rentals, rentalsHere, price, slabs = SLABS }: {
  t: TopUp; rentals: EnergyRental[]; rentalsHere: boolean; price: (quote: EnergyPreview) => ReactNode; slabs?: Slabs;
}) {
  const { s, quote, stale, pricesShown } = t;
  return (
    <>
      {s.problem !== null && <Said text={s.problem} />}
      {quote && quote.ok && quote.nothingToDo && <Said tone="neutral" prefix="Nothing to rent —" text={quote.reason ?? ''} />}
      {quote && quote.ok && !quote.nothingToDo && price(quote)}
      {quote && !quote.ok && <Said tone="warn" prefix="Not rented —" text={quote.reason ?? 'the desk gave no reason'} />}
      {/* A panel can be closed and opened again long after: the price says when it was asked — and, once the picture is old, that it may be. */}
      {s.quoted !== null && pricesShown && (stale
        ? <StateLine tone="warn">Priced at {stamp(s.quoted.atMs)} — may be out of date.</StateLine>
        : <p className="text-meta text-tg-hint">Priced at {stamp(s.quoted.atMs)}</p>)}
      <Outcome outcome={s.outcome} atMs={s.saidAtMs} rentals={rentals} rentalsHere={rentalsHere} slabs={slabs} />
    </>
  );
}

/**
 * Price a top-up, then place it. The quote on screen IS the order. (Its state and its rules: useTopUp, above.)
 */
function TopUp({ w, d, canChange, held, nowMs }: { w: EnergyWallet; d: EnergyStatus; canChange: boolean; held: boolean; nowMs: number }) {
  const t = useTopUp({ w, d, canChange, held, nowMs });
  const { s, price, rent, quote, pricing, heldBack, stale, onDesk, rentShown } = t;

  return (
    <div className="space-y-3 lg:max-w-3xl">
      <TopUpHeld t={t} w={w} />
      <TopUpFields t={t} d={d} />

      {/* What became of the order that got no answer, FIRST — it is why the button below says "the same order". */}
      <TopUpNoAnswer t={t} rentalsHere />
      <div className="flex flex-wrap items-center gap-2">
        {/* After an order with no answer, pricing again is a NEW order — the button says so; the one beside it sends the SAME one. */}
        <button type="button" className={`btn-secondary ${PANEL_BTN}`} disabled={pricing || s.sending} onClick={() => void price()}>
          {t.priceWord()}
        </button>
        {rentShown && quote !== null && quote.costSun !== null && (
          <button type="button" className={`${stale || onDesk ? 'btn-secondary' : 'btn-primary'} ${PANEL_BTN}`} disabled={s.sending || heldBack !== null}
            title={heldBack ?? undefined} onClick={() => void rent()}>
            {t.rentWord(quote.costSun)}
          </button>
        )}
      </div>

      <TopUpAnswer t={t} rentals={d.rentals} rentalsHere
        price={(quote) => (
          <div className="space-y-1">
            <p className="text-body">
              {units(quote.energy)} energy · {lasts(quote.durationSec)} · <span className="whitespace-nowrap font-semibold tabular">{quote.costSun === null ? '—' : trx(quote.costSun)} TRX</span>
              {dailyCostSun(quote) !== null && (
                <span className="whitespace-nowrap text-tg-hint"> · {rateTrx(String(dailyCostSun(quote)))} TRX a day</span>
              )}
            </p>
            <p className="text-meta text-tg-hint"><BurnSaves quote={quote} /></p>
            <WhyThisAmount quote={quote} />
            {!canChange && <p className="text-meta text-tg-hint">Ordering needs full access.</p>}
          </div>
        )} />
    </div>
  );
}

/**
 * What became of an order — in one of FIVE ways, and never "not rented" for
 * one that may have been placed, nor "ordered" for one the desk had already:
 * see lib/energyOrder.ts. Each is one line; the rest is behind its tap.
 * `rentalsHere`: the rentals are listed below (today's screen) — or on Energy, a tap away (the rebuilt pages).
 */
export function Outcome({ outcome, atMs, rentals, rentalsHere = true, slabs = SLABS }: {
  outcome: OrderOutcome | null; atMs: number | null; rentals: EnergyRental[]; rentalsHere?: boolean; slabs?: Slabs;
}) {
  if (outcome === null) return null;
  // DATED: the panel keeps what was said about its last order, and can be opened again hours later.
  const at = stamp(atMs);
  if (outcome.kind === 'ordered') {
    // Ordered is not delivered: amber until the provider fills it and the wallet shows the energy.
    return <StateLine tone="warn" more="It shows as delivered once the provider fills it and the wallet shows the energy.">Ordered at {at} — delivery pending.</StateLine>;
  }
  if (outcome.kind === 'already') {
    // Pressing again placed NOTHING: the desk had this order. What that rental is NOW is read from the list on screen.
    const listed = rentals.find((r) => r.id === outcome.rentalId);
    return (
      <StateLine tone="warn"
        more={
          <p>
            The desk already had this order: it is rental #{outcome.rentalId}
            {outcome.createdAt !== null && <>, written down at {fmtStamp(outcome.createdAt)}</>}
            {listed ? <> — {STATE_WORD[listed.state]}. {rentalsHere ? 'It is in the list below.' : 'It is in the list on Energy.'}</> : <> — {STATE_WORD[outcome.state as EnergyRentalState] ?? outcome.state} when the desk answered at {at}.</>}
          </p>
        }>
        Already ordered — rental #{outcome.rentalId}, {stateIn(listed ? listed.state : outcome.state)}. Nothing new was placed.
      </StateLine>
    );
  }
  if (outcome.kind === 'nothing') return <Said tone="neutral" prefix={`Nothing to rent (asked at ${at}) —`} text={outcome.reason} />;
  if (outcome.kind === 'unknown') {
    return (
      <OnSlab className={slabs.bad}>
        <StateLine tone="bad"
          more={
            <>
              <p>
                Not known whether it was rented. At {at} the desk answered that the order went out and it had no answer it could use
                (rental #{outcome.rentalId}, then {STATE_WORD[outcome.state as EnergyRentalState] ?? outcome.state}).
              </p>
              {rentalsHere
                ? <p>Check the provider&apos;s own order list, then settle it in the list below.</p>
                : <p>Check the provider&apos;s own order list, then settle it on Energy.</p>}
              <p>The desk said: {outcome.reason}</p>
            </>
          }>
          May have been placed and charged — do not order again.
        </StateLine>
      </OnSlab>
    );
  }
  return <Said prefix={`Not rented at ${at} —`} text={outcome.reason} />;
}

/** Does the desk renew this wallet's standing figure by itself? Only an active wallet's, and only while the desk's renewals are
 *  switched on (energy/src/desk.mjs skips any other wallet). */
export const standingRenews = (d: EnergyStatus, w: EnergyWallet): boolean =>
  w.status === 'active' && Number(w.standingEnergy) > 0 && d.policy.standingEnabled;

/** The words beside "Arrange" while another figure is typed: what is arranged is the SAVED figure. */
export const ARRANGE_UNSAVED = 'Unsaved — save first, then arrange.';

/**
 * A WALLET'S LABEL, STANDING ALLOCATION AND STATUS — its state and what may be done with it: one copy, for today's Edit panel and
 * the rebuilt settings page (screens/v2/energy/Settings.tsx). `onDone` when a change is saved (or there was nothing to save);
 * `onRefused` when the desk says no.
 */
export function useEditWallet({ w, d, onDone, onRefused }: { w: EnergyWallet; d: EnergyStatus; onDone: () => void; onRefused?: () => void }) {
  const qc = useQueryClient();
  const [standing, setStanding] = useState(units(w.standingEnergy));
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: { label?: string; standingEnergy?: string; status?: EnergyWallet['status'] }) => api.energyUpdateWallet(w.id, body),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['energy'] });
      if (res.ok) onDone(); else { onRefused?.(); setProblem(res.reason ?? 'Not saved.'); }
    },
  });
  // Keep the allocation in place NOW, once — the route when the desk's own renewals are switched off.
  // "Arrange it now" — in the store too (lib/energyOrder.ts, PART FOUR): one
  // key per arrangement, kept until the desk ANSWERS, so a press that got no
  // answer goes out again as the same order; and an arrangement on its way is
  // not forgotten when this panel is closed.
  //
  // The key belongs to the FIGURE it was sent for — the saved allocation, which
  // is what the desk rents. Once another figure has been saved, a press is a
  // new order under a new key, and the button says so.
  const a = useOrders().arranges[w.id] ?? arrangeInitial();
  const tell = (action: ArrangeAction) => orders.dispatch({ type: 'arrange', walletId: w.id, action });
  const figure = w.standingEnergy;
  const retry = arrangeRetries(a, figure);
  const arranging = useRef(false);
  const arrange = async () => {
    if (a.sending || arranging.current) return;
    arranging.current = true;
    const requestKey = arrangeKeyOf(a, mintKey('standing', w.id), figure);
    tell({ type: 'send', key: requestKey, figure });
    try {
      const res = await api.energyRent({ requestKey, walletId: w.id, purpose: 'standing', address: w.address });
      const outcome = orderOutcome(res, { requestKey, walletId: w.id });
      if (outcome === null) tell({ type: 'unanswered', why: UNREADABLE_ANSWER, atMs: Date.now() });
      else tell({ type: 'answered', outcome, atMs: Date.now() });
    } catch (e) {
      tell({ type: 'unanswered', why: errorWords(e), atMs: Date.now() });
    } finally {
      arranging.current = false;
      void qc.invalidateQueries({ queryKey: ['energy'] });
    }
  };
  const busy = save.isPending || a.sending;
  // "Arrange it now" rents the figure that is SAVED. With another one typed in
  // the field beside it, that is not what the operator is looking at.
  const unsaved = standingUnsaved(standing, w.standingEnergy);
  // Transfers a day, for the figure — and what keeping it would cost, estimated from the desk's price board and dated by it.
  const perDay = sendsADayOf(standing);
  const perDayChoice = perDay !== null && (SENDS_A_DAY as readonly number[]).includes(perDay) ? String(perDay)
    : standing.replace(/[,\s]/g, '') === '0' ? '0' : '';
  const estimate = standingEstimate({ energy: standing, prices: d.prices, energyFeeSun: d.burn?.energyFeeSun });
  // Not a live wallet in the Books' register: what would let the desk spend on it is held back.
  const hold = holdBack(registerOf(d, w));
  const renews = standingRenews(d, w);
  // "Arrange N now": for an active wallet with a standing figure. A NEW arrangement waits on the register; sending again one
  // that got no answer does not — the desk answers it with what became of it.
  const arrangeShown = w.status === 'active' && Number(w.standingEnergy) > 0;
  const arrangeOff = busy || unsaved || (hold !== null && !retry);
  const arrangeWhy = unsaved ? 'Save the new figure first' : hold !== null && !retry ? hold : undefined;
  // The SAVED figure, by name: that is what will be rented.
  const arrangeWord = a.sending ? 'Ordering…' : retry ? `Send the same order again (${units(w.standingEnergy)})` : `Arrange ${units(w.standingEnergy)} now`;

  /** Save's own checks, and what it would send — only what changed. (The rebuilt page shows it before Save: "Review settings".) */
  const checked = (): { problem: string } | { change: { standingEnergy?: string } } => {
    const digits = standing.replace(/[,\s]/g, '');
    if (!/^\d{1,9}$/.test(digits)) return { problem: 'Enter the standing figure as a whole number, or 0 for none.' };
    // Only what changed. A standing figure sent again unchanged is checked against the register as one being set. Its
    // name is the register's (nameOf, N-091): none is typed here.
    return { change: {
      ...(BigInt(digits) !== BigInt(w.standingEnergy) ? { standingEnergy: digits } : {}),
    } };
  };
  const submit = () => {
    tell({ type: 'clear' });
    const c = checked();
    if ('problem' in c) { setProblem(c.problem); return; }
    setProblem(null);
    if (Object.keys(c.change).length === 0) { onDone(); return; }
    save.mutate(c.change);
  };
  /** Status — each its own call to the desk, at once: Pause; Make active (held back by the register's no); Retire, confirmed. */
  const pause = () => save.mutate({ status: 'paused' });
  const activate = () => save.mutate({ status: 'active' });
  const retire = () => { if (confirm(`Retire ${nameOf(d, w)}? The desk stops renting for it and stops reading it.`)) save.mutate({ status: 'retired' }); };

  return {
    standing, setStanding, problem, setProblem, save, a, retry, arrange, busy, unsaved, perDayChoice, estimate, hold, renews,
    arrangeShown, arrangeOff, arrangeWhy, arrangeWord, checked, submit, pause, activate, retire,
  };
}
export type EditWalletState = ReturnType<typeof useEditWallet>;

/** The register's no to a wallet being edited, as one line — and that the desk still renews a figure it already holds. */
export function EditHold({ e, w, d }: { e: EditWalletState; w: EnergyWallet; d: EnergyStatus }) {
  const { hold, renews } = e;
  if (!hold) return null;
  return (
    <>
      <StateLine tone="warn"
        more={<p>{hold} From here it can be renamed, paused or retired, or its standing figure set to 0 — not made active, given a standing figure or topped up.</p>}>
        {registerLine(registerOf(d, w))} — no new energy.
      </StateLine>
      {/* The desk goes on spending on a figure it already holds: that is not held back by the register's no. */}
      {renews && (
        <StateLine tone="warn" more="It goes on until the figure is 0 or the wallet is paused.">The desk still renews its standing figure.</StateLine>
      )}
    </>
  );
}

/** Transfers a day — a figure for the standing energy (one above the desk's largest order is shown, and cannot be chosen). */
export function TransfersADay({ e, d, onPick }: { e: EditWalletState; d: EnergyStatus; onPick: (standing: string) => void }) {
  return (
    <select className="select" value={e.perDayChoice} disabled={e.busy}
      onChange={(ev) => { if (ev.target.value !== '') onPick(units(String(energyForSendsADay(Number(ev.target.value))))); }}>
      <option value="">Choose, or type a figure</option>
      <option value="0">None</option>
      {SENDS_A_DAY.map((n) => (
        <option key={n} value={String(n)} disabled={energyForSendsADay(n) > BigInt(d.policy.maxEnergy)}>
          {n} transfers a day — {units(String(energyForSendsADay(n)))}
        </option>
      ))}
    </select>
  );
}

/** What keeping the figure would cost — an estimate from the desk's price board, dated by it. */
export function StandingCost({ e }: { e: EditWalletState }) {
  const { estimate } = e;
  if (!estimate) return null;
  return (
    <div className="space-y-0.5 text-meta text-tg-hint">
      <p>
        About <span className="font-semibold tabular text-tg-text">{trx(String(estimate.costSun))} TRX</span> per {estimate.days === 1 ? 'day' : `${estimate.days} days`}
        {' '}· {rateTrx(String(estimate.perSendSun))} TRX a transfer
      </p>
      <p>{estimate.burnSun !== null && <>Burning {trx(String(estimate.burnSun))} TRX · </>}Estimate · price board {fmtStamp(estimate.atIso)}</p>
    </div>
  );
}

/**
 * An arrangement that got no answer. The same figure again is the SAME order; another figure saved since is a NEW one — the key
 * went with the figure. `rentalsHere`: the line that follows it is at the top of this screen (today's) — or on Energy.
 */
export function ArrangeNoAnswer({ e, rentalsHere }: { e: EditWalletState; rentalsHere: boolean }) {
  const { a, retry } = e;
  return (
    <>
      {retry && (
        <StateLine tone="bad"
          more={
            <>
              <p>{a.unanswered}</p>
              <p>No usable answer came back (at {stamp(a.saidAtMs)}), so the order may or may not have reached the desk. Pressing again sends the SAME order — it cannot be placed twice.</p>
            </>
          }>
          No answer at {stamp(a.saidAtMs)} — the button re-sends the same order.
        </StateLine>
      )}
      {!retry && a.unanswered !== null && !a.sending && (
        <StateLine tone="bad"
          more={
            <>
              <p>{a.unanswered}</p>
              <p>
                An arrangement of {units(a.figure)} got no answer at {stamp(a.saidAtMs)} — {rentalsHere ? 'the note at the top of this screen follows it' : 'the line on Energy follows it'}.
                {' '}Another figure has been saved since, so the button above starts a NEW order.
              </p>
            </>
          }>
          {units(a.figure)} got no answer — the button starts a new order.
        </StateLine>
      )}
    </>
  );
}

/** A wallet's standing allocation and status — its name is the register's. (Its state and its rules: useEditWallet, above.) */
function EditWallet({ w, d, onDone }: { w: EnergyWallet; d: EnergyStatus; onDone: () => void }) {
  const e = useEditWallet({ w, d, onDone });
  const { standing, setStanding, problem, save, a, busy, unsaved, hold } = e;

  return (
    <div className="space-y-3 lg:max-w-3xl">
      <EditHold e={e} w={w} d={d} />
      <FieldRow>
        <Field label="Transfers a day">
          <TransfersADay e={e} d={d} onPick={setStanding} />
        </Field>
        <Field label="Standing energy" hint="0 for none">
          <input className="input" inputMode="numeric" value={standing} disabled={busy} onChange={(ev) => setStanding(ev.target.value)} />
        </Field>
      </FieldRow>
      <StandingCost e={e} />
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={`btn-secondary ${PANEL_BTN}`} disabled={busy} onClick={e.submit}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {w.status === 'active'
          ? <button type="button" className={`btn-secondary ${PANEL_BTN}`} disabled={busy} onClick={e.pause}>Pause</button>
          : <button type="button" className={`btn-secondary ${PANEL_BTN}`} disabled={busy || hold !== null}
              title={hold ?? undefined} onClick={e.activate}>Make active</button>}
        {w.status !== 'retired' && (
          <button type="button" className={`btn-secondary text-neg ${PANEL_BTN}`} disabled={busy} onClick={e.retire}>
            Retire
          </button>
        )}
        {e.arrangeShown && (
          <button type="button" className={`btn-secondary ${PANEL_BTN}`} disabled={e.arrangeOff}
            title={e.arrangeWhy} onClick={() => void e.arrange()}>
            {e.arrangeWord}
          </button>
        )}
      </div>
      {unsaved && e.arrangeShown && <p className="text-meta text-tg-hint">{ARRANGE_UNSAVED}</p>}
      {problem !== null ? <Said text={problem} /> : save.isError ? <Said text={errorWords(save.error)} /> : null}
      <ArrangeNoAnswer e={e} rentalsHere />
      <Outcome outcome={a.outcome} atMs={a.saidAtMs} rentals={d.rentals} />
    </div>
  );
}

/**
 * LIST A WALLET — its state and what may be done: one of the Books' register, PICKED, never typed (operator, 4 Oct 2026,
 * D6), under the register's name (N-091: nobody types a label); the same three refusals in words. One copy, for today's box and
 * the rebuilt sheet (components/v2/energy/AddWalletSheet.tsx). `candidates` is null when the register could not be read.
 */
export function useAddWallet({ d, onDone }: { d: EnergyStatus; onDone: () => void }) {
  const qc = useQueryClient();
  const register = d.register;
  const candidates = register !== undefined && !('error' in register) ? register.candidates : null;
  const [picked, setPicked] = useState('');
  const [standing, setStanding] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: (body: { booksWalletId: number; label: string; standingEnergy?: string }) => api.energyAddWallet(body),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['energy'] });
      if (res.ok) onDone(); else setProblem(res.reason ?? 'Not added.');
    },
  });

  const chosen = candidates?.find((c) => String(c.booksWalletId) === picked) ?? null;
  const pick = (id: string) => {
    setPicked(id);
    setProblem(null);
  };
  const submit = () => {
    const digits = standing.replace(/[,\s]/g, '');
    if (chosen === null) { setProblem('Choose a wallet from the Books\' register.'); return; }
    if (digits !== '' && !/^\d{1,9}$/.test(digits)) { setProblem('Enter the standing figure as a whole number, or leave it empty.'); return; }
    setProblem(null);
    // Listed under the register's name — the desk keeps a label of at most 60 characters.
    add.mutate({ booksWalletId: chosen.booksWalletId, label: chosen.name.trim().slice(0, 60), ...(digits ? { standingEnergy: digits } : {}) });
  };
  return { candidates, picked, pick, standing, setStanding, problem, add, chosen, submit };
}
export type AddWalletState = ReturnType<typeof useAddWallet>;

/** Every live wallet of the register is listed already: a new one is added to the register in the Books first. */
export const AllListed = () => (
  <StateLine tone="neutral" more="A new wallet is added to the register in the Books first; then it can be listed here.">Every live wallet is listed.</StateLine>
);

/** The whole address of what was picked — the one the desk will list — to be read before it is added. */
export function WillList({ d, address }: { d: EnergyStatus; address: string }) {
  return (
    <p className="text-meta text-tg-hint flex flex-wrap items-center gap-x-1.5">
      It will list <span className="font-mono text-tg-text break-all">{address}</span>
      <a href={explorerAddress(d.network, address)} target="_blank" rel="noreferrer" className="tap-44 text-info hover:text-tg-text"
        title="Open in the chain explorer" aria-label="Open in the chain explorer">
        <ExternalLink size={12} />
      </a>
    </p>
  );
}

/** List a wallet: one of the Books' register, picked — never typed (operator, 4 Oct 2026, D6). (useAddWallet, above.) */
function AddWallet({ d, onDone }: { d: EnergyStatus; onDone: () => void }) {
  const f = useAddWallet({ d, onDone });
  const { candidates, picked, pick, standing, setStanding, problem, add, chosen } = f;

  const BOX = 'px-3 py-3 space-y-3 border-b border-stroke-soft bg-tg-secondary/30';
  // An unreadable register is said ONCE, above the list, and the button is off: nothing to draw here.
  if (candidates === null) return null;
  if (candidates.length === 0) {
    return (
      <div className={BOX}>
        <AllListed />
      </div>
    );
  }

  return (
    <div className={BOX}>
      <FieldRow>
        <Field label="Wallet">
          <select className="select" value={picked} onChange={(e) => pick(e.target.value)}>
            <option value="">Choose a wallet</option>
            {candidates.map((c) => (
              <option key={c.booksWalletId} value={String(c.booksWalletId)}>{c.name} — {mask(c.address)}</option>
            ))}
          </select>
        </Field>
        <Field label="Standing energy" hint="optional">
          <input className="input" inputMode="numeric" value={standing} onChange={(e) => setStanding(e.target.value)} />
        </Field>
      </FieldRow>
      {/* The whole address of what was picked — the one the desk will list — to be read before it is added. */}
      {chosen && <WillList d={d} address={chosen.address} />}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={`btn-secondary ${PANEL_BTN}`} disabled={add.isPending} onClick={f.submit}>
          {add.isPending ? 'Adding…' : 'Add'}
        </button>
      </div>
      {problem !== null ? <Said text={problem} /> : add.isError ? <Said text={errorWords(add.error)} /> : null}
    </div>
  );
}

// ── THE REDESIGNED LIST'S WORDS (his words, 30 Oct 2026: "yes redesign properly- and label clearly"; he could not tell where TRX
// goes, or which wallets need energy) — one copy, for the rebuilt list (components/v2/energy/BalanceBox, screens/v2/energy):
//   balanceState                 the TronSave balance's state: not read (TronSave not answering — the figure is the last one
//                                read), not read yet, below the minimum (criticalSun), low (lowSun), or nothing to say
//   balanceLine / balanceFacts   its one warning (not answering, the key refused, never read — and that nothing is ordered), and
//                                the facts under the figure, one a line: when it was last read, the line it is under
//   walletNeed                   what one desk wallet needs, as a FIGURE (his "one fact per cell", 1 Nov 2026): the energy it is
//                                short, 0 when it has enough, or null — it cannot be said, and the wallet's tag says why
//   listed                       the wallets the list shows (active) and the ones folded into one line under it (paused, retired)
export type BalanceKey = 'unread' | 'waiting' | 'below' | 'low' | 'ok';
export interface BalanceState { key: BalanceKey; word: string | null; tone: 'bad' | 'warn' | 'neutral' }

export function balanceState(p: EnergyStatus['provider']): BalanceState {
  if (p.error !== null) return { key: 'unread', word: 'Not read', tone: 'bad' };
  if (p.balanceSun === null) return { key: 'waiting', word: 'Not read yet', tone: 'neutral' };
  const n = Number(p.balanceSun);
  if (n < Number(p.criticalSun)) return { key: 'below', word: 'Below minimum', tone: 'bad' };
  if (n < Number(p.lowSun)) return { key: 'low', word: 'Low', tone: 'warn' };
  return { key: 'ok', word: null, tone: 'neutral' };
}

/** The balance's one warning: TronSave not answering, the desk's key refused, or the balance never read — and that, while so,
 *  nothing is ordered. Otherwise null: a low balance is said by its tag and the line it is under (balanceFacts). */
export function balanceLine(p: EnergyStatus['provider'], s: BalanceState): string | null {
  if (s.key !== 'unread') return null;
  // A key TronSave refuses is said to be one: TronSave is answering (live, 29–30 Oct 2026, for a day).
  const refused = /INVALID_API_KEY|\bapi key\b/i.test(p.error ?? '');
  if (refused) return 'TronSave refuses the desk\'s key — no orders.';
  return p.checkedAt ? 'TronSave not answering — no orders.' : 'TronSave balance never read — no orders.';
}

/**
 * The facts under the balance, one a line; `when` writes a Dubai time. Not read: the LAST GOOD read (checkedAt) — the figure above
 * is that read's; errorAt is the latest failure, moved on by every pass (live, 30 Oct 2026, it would have said "30 Oct 12:54" of
 * reads failing since 29 Oct 13:01). Low, or below the minimum: the line it is under. A figure comes with its unit apart (TRX).
 */
export function balanceFacts(p: EnergyStatus['provider'], s: BalanceState, when: (iso: string) => string): Array<{ label: string; value: string; unit: 'TRX' | null }> {
  if (s.key === 'unread') return p.checkedAt ? [{ label: 'Last read', value: when(p.checkedAt), unit: null }] : [];
  if (s.key === 'below') return [{ label: 'Minimum', value: trx(p.criticalSun), unit: 'TRX' }];
  if (s.key === 'low') return [{ label: 'Low below', value: trx(p.lowSun), unit: 'TRX' }];
  return [];
}

/**
 * What a wallet needs, as a figure: what it should have — one transfer, or its standing figure if larger — less what it has, and 0
 * when it has that. Null when it cannot be said — retired, the chain unread, not on-chain, not read yet: the wallet's tag says which.
 */
export function walletNeed(w: EnergyWallet): bigint | null {
  if (w.status === 'retired') return null;
  if (w.observeError) return null;
  if (w.activated === false || w.available === null) return null;
  const standing = BigInt(w.standingEnergy || '0');
  const target = standing > SEND_ENERGY ? standing : SEND_ENERGY;
  const short = target - BigInt(w.available);
  return short > 0n ? short : 0n;
}

/** The list shows the active wallets; paused and retired fold into one line under it. */
export function listed(wallets: readonly EnergyWallet[]): { shown: EnergyWallet[]; folded: EnergyWallet[]; foldWords: string | null } {
  const shown = wallets.filter((w) => w.status === 'active');
  const folded = wallets.filter((w) => w.status !== 'active');
  const paused = folded.filter((w) => w.status === 'paused').length, retired = folded.length - paused;
  const foldWords = folded.length === 0 ? null : [paused ? `${paused} paused` : '', retired ? `${retired} retired` : ''].filter(Boolean).join(' · ');
  return { shown, folded, foldWords };
}
